import { Head } from '@inertiajs/react'
import { FormEvent, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useRef, useState } from 'react'

import verbData from '@/data/verbs.json'
import { commonPrefixLength, endingTypeOf, PRONOUN_KEYS, REGULAR_ENDINGS, type EndingType } from './conjugation'
import { conjugationGridFor, gerundAndParticiples } from './conjugator'
import {
  buildComparativeSession,
  COMPARATIVE_CATEGORIES,
  COMPARATIVE_FORMS,
  COMPARATIVE_SESSION_LENGTH,
  comparativePatternCount,
  INITIAL_COMPARATIVE_CATEGORY_IDS,
  INITIAL_COMPARATIVE_FORM_IDS,
  type ComparativeCategory,
  type ComparativeForm,
} from './comparativeDrill'
import {
  buildContractionSession,
  CONTRACTION_CATEGORIES,
  contractionPromptCount,
  CONTRACTION_PREPS,
  CONTRACTION_TABLE,
  contractionSubtypes,
  COM_PRONOUN_FORMS,
  COM_TWO_WORD_FORMS,
  PRONOUN_CONTRACTIONS,
  DEFAULT_CONTRACTION_IDS,
  DEFAULT_HINT_LEVEL,
  onlyPronounCards,
  possessionNote,
  toggleContractionCard,
  type ContractionGroup,
  type ContractionRow,
  type HintLevel,
} from './contractionDrill'
import { autocomplete, DICTIONARY, lookup, wordByWordBreakdown, type AutocompleteCandidate, type DictSense, type ResultGroup } from './dictionary'
import { buildDrillPoolFor, showStemChangeHint, stemChangeExplanation, type StemChange } from './drillPool'
import {
  buildObjectPronounSession,
  clusterOf,
  DEFAULT_OP_CATEGORY_IDS,
  DEFAULT_OP_PLACEMENT_IDS,
  OP_CATEGORIES,
  OP_PLACEMENTS,
  OP_REGISTERS,
  opPool,
  sentenceWith,
  type OPCategory,
  type Placement,
  type Register,
} from './objectPronounDrill'
import { createPhrasePicker, shuffle, type PhraseCategory } from './phraseBank'
import { firstEmbeddableRecording, matchesSongQuery, SONGS, youTubeEmbedUrl, type Song } from './songs'
import {
  buildPluralsSession,
  DEFAULT_PLURAL_CARD_IDS,
  PLURAL_CARDS,
  PLURALS_SESSION_LENGTH,
  pluralPromptCount,
  togglePluralCard,
  type PluralCardId,
} from './pluralsDrill'
import { DEIXIS_DECISION, DEIXIS_ROWS, DIMINUTIVE_DECISION, DIMINUTIVE_ROWS, MUITO_COLUMNS, MUITO_DECISION, OLD_DECISION, OLD_QUICK_TEST, OLD_ROWS, OLD_WORDS, TIMING_COLUMNS, TIMING_DECISION, TIMING_ROWS } from './nuances'
import { englishQuestion, patternForSubject, PLURAL_SUBJECTS, resolveTranslation } from './translation'
import {
  BE_VERBS,
  BE_QUICK_TEST,
  BE_ROWS,
  CAN_ROWS,
  IRREGULAR_PARTICIPLES,
  PARTICIPLE_RULE,
  PERFECT_QUICK_TEST,
  PERFECT_ROWS,
} from './verbChoice'
import { TRANSITION_GROUPS, TRANSITIONS } from './transitions'
import { baseVerb, naturalPool, splitPrompt, type NaturalItem } from './naturalSubjunctive'
import { FALSE_FRIENDS, VOCAB_CATEGORIES, VOCAB_CATEGORY_LABEL, VOCAB_LEVELS, VOCABULARY } from './vocabulary'

type Pronoun = 'eu' | 'tu' | 'você' | 'nós' | 'eles'
// What the drill setup offers: the five columns plus "a gente" (see drillPool.ts).
type DrillPronoun = Pronoun | 'a gente'
type DrillTense = 'present' | 'preterite' | 'imperfect' | 'presentSubjunctive' | 'future'
// Every column shown in the reference tables. The drill only ever uses the four
// `DrillTense` values.
type TableColumn =
  | 'present'
  | 'preterite'
  | 'imperfect'
  | 'future'
  | 'conditional'
  | 'futureProgressive'
  | 'presentSubjunctive'
  | 'imperfectSubjunctive'
  | 'futureSubjunctive'
  | 'imperativeAffirmative'
  | 'imperativeNegative'
type VerbType = 'regular-ar' | 'regular-er' | 'regular-ir' | 'stem-changing' | 'irregular'
type ComplementType = 'fixed' | 'objectSlot' | 'none'
type Screen = 'setup' | 'drill' | 'complete'
// Grammar topics shown as chips inside the Practice and Grammar tabs — each
// has both a drill and a cheat sheet. New topics are added here, never as new
// top-level tabs.
type Topic = 'conjugation' | 'contraction' | 'objectPronoun' | 'plurals' | 'comparative' | 'subjunctive' | 'nuances' | 'verbChoice'

type VerbTemplate = {
  id: string
  complementType: ComplementType
  fixedComplement: string | null
  englishVerb: string
  englishTranslationPattern: string
  // Restricts the random time/location/manner tail to a subset — absent
  // means all three, as before (see phraseBank.ts's PhraseCategory).
  allowedPhrases?: PhraseCategory[]
  // Agreement forms for a predicate adjective/noun fixedComplement (ser +
  // estudante, estar + cansado, ficar + feliz) — see agreeComplement below.
  fixedComplementFeminine?: string
  fixedComplementPlural?: string
  fixedComplementPluralFeminine?: string
  // English pattern used instead for a plural subject ("… students").
  englishTranslationPatternPlural?: string
}

type Verb = {
  infinitive: string
  englishMeaning: string
  verbType: VerbType
  stemChangePattern: string | null
  stemChange?: StemChange
  conjugations: Record<TableColumn, Partial<Record<Pronoun, string>>>
  templates: VerbTemplate[]
}

// The four tenses with an English counterpart (the present subjunctive is drilled
// separately, from fixed spoken sentences — see naturalSubjunctive.ts).
type IndicativeTense = Exclude<DrillTense, 'presentSubjunctive'>

type Settings = {
  tenses: DrillTense[]
  verbTypes: VerbType[]
  pronouns: DrillPronoun[]
}

// One normalized prompt for the shared drill shell, whatever the topic. The
// sentence flows: beforeWords · [answer input] · (cueText) · afterWords.
type DrillItem = {
  topic: Topic
  badge: string
  beforeWords: string[]
  afterWords: string[]
  cueText: string // shown in parentheses after the blank: "(ir)" / "(a + a)"
  answer: string
  sentence: string // the completed sentence, for TTS + the correct-answer render
  translation: string
  answerAriaLabel: string
  disclaimer: string
  suffix?: string // punctuation that hugs the answer, e.g. "?" on a question prompt
  stemChangeVerb?: Verb // conjugation stem-changer → the cue becomes an info popover
  promptLines?: string[] // the question (or English "say: …") shown above the answer line (object-pronoun drill)
  acceptedAnswers?: string[] // all forms graded correct; defaults to [answer]
  clusterOf?: (typed: string) => string // reduces what was typed to the graded part (object-pronoun drill: a whole sentence typed in the blank)
  sentenceFor?: (reply: string) => string // the completed sentence for an accepted reply, for the audio
  contrast?: { written: string; spoken: string; active: Register } // "Written: … · Spoken: …" feedback
  answerNote?: (typed: string) => string | null // one line shown under a wrong answer (contraction drill: the possession ordering rule)
  tip?: string // one line shown beside the correct answer after a miss (plural drill)
  tipRows?: string[] // optional rule rows listed under the tip, one per row (plural drill)
  speaksAnswerOnly?: boolean // the audio is just the answer, so the read-aloud highlight stays on the answer slot (plural drill)
  vocabHints?: Array<{ en: string; pt: string }> // optional, opt-in word lookups for prompt vocab the learner may not know yet
}

type DrillScore = { correct: number; total: number }

const verbs = verbData.verbs as Verb[]

const DRILL_TENSES: Array<{ id: DrillTense; label: string; badge: string }> = [
  { id: 'present', label: 'Present', badge: 'Presente' },
  { id: 'preterite', label: 'Past', badge: 'Pretérito' },
  { id: 'imperfect', label: 'Imperfect', badge: 'Pretérito imperfeito' },
  { id: 'presentSubjunctive', label: 'Present subjunctive', badge: 'Presente do subjuntivo' },
  { id: 'future', label: 'Future', badge: 'Futuro' },
]

const TABLE_COLUMNS: Array<{ id: TableColumn; label: string }> = [
  { id: 'present', label: 'Present' },
  { id: 'preterite', label: 'Past' },
  { id: 'imperfect', label: 'Imperfect' },
  { id: 'presentSubjunctive', label: 'Present subjunctive' },
  { id: 'future', label: 'Future' },
  { id: 'conditional', label: 'Conditional' },
  { id: 'imperativeAffirmative', label: 'Affirmative imperative' },
  { id: 'imperativeNegative', label: 'Negative imperative' },
]

const VERB_TYPES: Array<{ id: VerbType; label: string }> = [
  { id: 'regular-ar', label: 'Regular -ar' },
  { id: 'regular-er', label: 'Regular -er' },
  { id: 'regular-ir', label: 'Regular -ir' },
  { id: 'stem-changing', label: 'Stem-changing' },
  { id: 'irregular', label: 'Irregular' },
]

// Consistent você→ela→ele ordering, singular and plural, in both the drill
// setup checkboxes and the reference-table row headers.
const PRONOUNS: Array<{ id: Pronoun; label: string }> = [
  { id: 'eu', label: 'eu' },
  { id: 'tu', label: 'tu' },
  { id: 'você', label: 'você / ela / ele' },
  { id: 'nós', label: 'nós' },
  { id: 'eles', label: 'vocês / elas / eles' },
]

// The drill setup's pronoun chips: the table pronouns, with "a gente" right after nós.
const DRILL_PRONOUNS: Array<{ id: DrillPronoun; label: string }> = PRONOUNS.flatMap((pronoun) =>
  pronoun.id === 'nós' ? [pronoun, { id: 'a gente' as const, label: 'a gente' }] : [pronoun]
)

const PT_SUBJECTS: Record<DrillPronoun, string[]> = {
  eu: ['Eu'],
  tu: ['Tu'],
  você: ['Você', 'Ele', 'Ela'],
  nós: ['Nós'],
  'a gente': ['A gente'],
  eles: ['Vocês', 'Eles', 'Elas'],
}

const DEFAULT_SETTINGS: Settings = {
  tenses: ['present'],
  verbTypes: ['regular-ar', 'regular-er', 'regular-ir'],
  pronouns: ['eu', 'você', 'a gente', 'eles'],
}

// ---------------------------------------------------------------------------
// Conjugation Tables — regular-vs-irregular highlighting (computed at render)
// ---------------------------------------------------------------------------

type FormSegment = { text: string; kind: 'plain' | 'ending' | 'irregular' }

// Split one conjugated form into coloured segments: the regular pronoun ending
// (blue), any part that departs from the expected regular pattern (amber), and
// the untouched stem (plain).
function analyzeForm(infinitive: string, column: TableColumn, pronounIndex: number, value: string): FormSegment[] {
  if (!value || value === '—') return [{ text: value || '—', kind: 'plain' }]

  const segments: FormSegment[] = []
  let form = value
  if (form.startsWith('não ')) {
    segments.push({ text: 'não ', kind: 'plain' })
    form = form.slice(4)
  }

  const regularEnding = REGULAR_ENDINGS[endingTypeOf(infinitive)][column]?.[pronounIndex] ?? null
  if (!regularEnding) {
    segments.push({ text: form, kind: 'plain' })
    return segments
  }

  const stem = infinitive.slice(0, -2)
  const expected = stem + regularEnding
  const splitAt = form.length - regularEnding.length
  const actualEnding = form.slice(Math.max(splitAt, 0))
  const actualStem = splitAt > 0 ? form.slice(0, splitAt) : ''

  if (actualEnding === regularEnding) {
    const shared = commonPrefixLength(actualStem, stem)
    if (shared > 0) segments.push({ text: actualStem.slice(0, shared), kind: 'plain' })
    if (shared < actualStem.length) segments.push({ text: actualStem.slice(shared), kind: 'irregular' })
    segments.push({ text: actualEnding, kind: 'ending' })
    return segments
  }

  // The ending itself breaks the pattern — treat the form as irregular, keeping
  // only the stem-length matching prefix plain.
  const shared = Math.min(commonPrefixLength(form, expected), stem.length)
  if (shared > 0) segments.push({ text: form.slice(0, shared), kind: 'plain' })
  segments.push({ text: form.slice(shared), kind: 'irregular' })
  return segments
}

function FormCell({ infinitive, column, pronounIndex, value }: { infinitive: string; column: TableColumn; pronounIndex: number; value: string }) {
  return (
    <>
      {analyzeForm(infinitive, column, pronounIndex, value).map((segment, index) => (
        <span
          key={index}
          className={segment.kind === 'ending' ? 'font-bold text-blue-700' : segment.kind === 'irregular' ? 'font-bold text-amber-600' : undefined}
        >
          {segment.text}
        </span>
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// Drill sentence building
// ---------------------------------------------------------------------------

function toggleValue<T>(values: T[], value: T) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Answer comparison shared by every drill: case-fold (pt) but accent-exact,
// forgiving of trailing punctuation and repeated inner spaces.
function normalizeForCompare(value: string): string {
  return value
    .normalize('NFC')
    .toLocaleLowerCase('pt')
    .trim()
    .replace(/[.!?,;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// Break a prompt around its blank: `before` ends with a space, `after` starts
// with one. Words are pre-split for the karaoke highlight; `sentence` is the
// completed string used for TTS.
function splitAround(before: string, answer: string, after: string) {
  return {
    beforeWords: before.trim().split(/\s+/).filter(Boolean),
    afterWords: after.trim().split(/\s+/).filter(Boolean),
    sentence: `${before}${answer}${after}`,
  }
}

const CONJUGATION_DISCLAIMER = 'Example sentences are generated to practice conjugation and may not always make logical sense.'

// Tense-scoped selection lives in drillPool; a stem-changing verb only lands
// here for the exact forms that actually alternate. The present subjunctive isn't
// part of the verb × person pool: it has its own bank of fixed spoken sentences,
// each with the persons it naturally takes.
function drillPool(settings: Settings) {
  const conjugation = buildDrillPoolFor(verbs, settings).flatMap((item) =>
    item.tense === 'presentSubjunctive' ? [] : [{ ...item, tense: item.tense }]
  )
  const natural = settings.tenses.includes('presentSubjunctive') ? naturalPool(settings) : []
  return { conjugation, natural }
}

// Roughly this share of a session's prompts are WH-questions rather than
// statements (when the drawn verb supports at least one question frame).
const QUESTION_SHARE = 0.4

// The high-frequency question openers. `wh` is the Portuguese opener (sentence-
// initial); `whEn` / `whTrailing` build the English gloss. `allow`, when set,
// restricts the frame to verbs that read naturally with it; `needsObjectSlot`
// marks frames where the WH word is itself the direct object.
type QuestionFrame = { wh: string; whEn: string; whTrailing?: string; allow?: Set<string>; needsObjectSlot?: boolean }
const QUESTION_FRAMES: QuestionFrame[] = [
  { wh: 'Onde', whEn: 'Where' },
  { wh: 'Quando', whEn: 'When' },
  { wh: 'Por que', whEn: 'Why' },
  { wh: 'Como', whEn: 'How' },
  {
    wh: 'Com quem',
    whEn: 'Who',
    whTrailing: 'with',
    allow: new Set(['morar', 'estudar', 'trabalhar', 'falar', 'dançar', 'cantar', 'jogar', 'viajar', 'comer', 'beber', 'discutir', 'andar', 'correr', 'ir', 'vir', 'partir']),
  },
  // "Com o que você trabalha?" — asking what someone does for a living, not
  // who they work with (that's "Com quem" above).
  { wh: 'Com o que', whEn: 'What', whTrailing: 'with', allow: new Set(['trabalhar']) },
  { wh: 'O que', whEn: 'What', needsObjectSlot: true },
]
// Verbs whose bare "Onde/Quando/Por que/Como …?" reads as broken rather than
// merely quirky (ser wants "De onde …?"; estar is fine and stays). They can
// still surface as an "O que …?" object question.
const QUESTION_VERB_EXCLUDE = new Set(['ser', 'ter', 'poder', 'conseguir', 'saber', 'querer', 'preferir', 'sentir', 'meter', 'permitir', 'medir', 'conhecer', 'insistir', 'ouvir', 'entender', 'dividir', 'descrever'])
// Their objectSlot complement is an infinitive phrase ("ajudar você", "em
// ajudar"), not a plain noun — "O que ele pode?" / "O que ele insiste?" reads
// as incomplete without an explicit "fazer"/"em", so they're excluded even
// though they do have an objectSlot template (for statement prompts).
const QUESTION_OBJECT_EXCLUDE = new Set(['poder', 'conseguir', 'insistir'])

function framesForVerb(verb: Verb): QuestionFrame[] {
  const hasObjectSlot = verb.templates.some((template) => template.complementType === 'objectSlot')
  return QUESTION_FRAMES.filter((frame) => {
    if (frame.needsObjectSlot) return hasObjectSlot && !QUESTION_OBJECT_EXCLUDE.has(verb.infinitive)
    if (QUESTION_VERB_EXCLUDE.has(verb.infinitive)) return false
    if (frame.allow) return frame.allow.has(verb.infinitive)
    return true
  })
}

const FEMININE_SUBJECTS = new Set(['Ela', 'Elas'])

// A "fixed" complement is usually a direct object (pizza, a carta) and never
// agrees with the subject. The few that are predicate adjectives/nouns
// describing the subject itself (ser + estudante, estar + cansado, ficar +
// feliz) do need to: "Nós estamos cansado" / "Ela está cansado" are not real
// Portuguese. The template's plain fixedComplement is the masculine
// singular default; these optional fields supply the other three forms only
// where the word actually changes (estudante/feliz don't change for gender).
function agreeComplement(template: VerbTemplate, subject: string): string {
  const plural = PLURAL_SUBJECTS.has(subject)
  const feminine = FEMININE_SUBJECTS.has(subject)
  if (plural && feminine) return template.fixedComplementPluralFeminine ?? template.fixedComplementPlural ?? template.fixedComplement ?? ''
  if (plural) return template.fixedComplementPlural ?? template.fixedComplement ?? ''
  if (feminine) return template.fixedComplementFeminine ?? template.fixedComplement ?? ''
  return template.fixedComplement ?? ''
}

function buildStatementItem(
  verb: Verb,
  tense: IndicativeTense,
  subject: string,
  conjugated: string,
  pick: ReturnType<typeof createPhrasePicker>,
): DrillItem {
  const template = verb.templates[Math.floor(Math.random() * verb.templates.length)]

  let tail: string
  let translation: string
  if (template.complementType === 'objectSlot') {
    // A verb-appropriate object fills the empty complement slot directly
    // (keyed by the template's englishVerb, not one generic noun bank).
    const object = pick.object(template.englishVerb)
    tail = object.pt
    translation = `${capitalize(resolveTranslation(patternForSubject(template, subject), template.englishVerb, subject, tense, object.en))}.`
  } else if (template.complementType === 'fixed' && template.allowedPhrases?.length === 0) {
    // allowedPhrases: [] opts out of the trailing time/location/manner phrase
    // entirely — for a complement that's already a complete statement on its
    // own (an age: "Tenho vinte e nove anos"), tacking one on reads as
    // nonsense ("...at home" / "...quickly").
    tail = agreeComplement(template, subject)
    translation = `${capitalize(resolveTranslation(patternForSubject(template, subject), template.englishVerb, subject, tense))}.`
  } else {
    // "fixed" keeps its own complement and a time/location/manner phrase tacks
    // on after it; "none" is just the phrase.
    const phrase = pick.phrase(tense, template.allowedPhrases)
    tail = template.complementType === 'fixed' ? `${agreeComplement(template, subject)} ${phrase.pt}` : phrase.pt
    translation = `${capitalize(resolveTranslation(patternForSubject(template, subject), template.englishVerb, subject, tense))} ${phrase.en}.`
  }

  return {
    topic: 'conjugation',
    badge: DRILL_TENSES.find((entry) => entry.id === tense)?.badge ?? '',
    cueText: verb.infinitive,
    answer: conjugated,
    translation,
    answerAriaLabel: `Conjugate ${verb.infinitive}`,
    disclaimer: CONJUGATION_DISCLAIMER,
    stemChangeVerb: verb.stemChange ? verb : undefined,
    ...splitAround(`${subject} `, conjugated, ` ${tail}.`),
  }
}

function buildQuestionItem(verb: Verb, tense: IndicativeTense, subject: string, conjugated: string, frames: QuestionFrame[]): DrillItem {
  const frame = frames[Math.floor(Math.random() * frames.length)]
  const template = frame.needsObjectSlot
    ? verb.templates.find((entry) => entry.complementType === 'objectSlot') ?? verb.templates[0]
    : verb.templates.find((entry) => entry.englishVerb !== 'can' && entry.englishVerb !== 'manage') ?? verb.templates[0]

  // "Onde você mora?" — WH opener + lower-cased subject + the blank + "?".
  const split = splitAround(`${frame.wh} ${subject.toLocaleLowerCase('pt')} `, conjugated, '')
  return {
    topic: 'conjugation',
    badge: DRILL_TENSES.find((entry) => entry.id === tense)?.badge ?? '',
    cueText: verb.infinitive,
    answer: conjugated,
    translation: englishQuestion(frame.whEn, frame.whTrailing ?? null, template.englishVerb, subject, tense),
    answerAriaLabel: `Conjugate ${verb.infinitive}`,
    disclaimer: CONJUGATION_DISCLAIMER,
    suffix: '?',
    stemChangeVerb: verb.stemChange ? verb : undefined,
    beforeWords: split.beforeWords,
    afterWords: split.afterWords,
    sentence: `${split.sentence}?`,
  }
}

// A sentence Brazilians actually say, with the verb in the blank ("Tomara que ___
// certo. (dar)"). The imperative family notes that commands are subjunctive forms.
function buildNaturalItem(item: NaturalItem): DrillItem {
  const { before, after } = splitPrompt(item.prompt)
  // Punctuation right after the blank ("?", ",") hugs the answer, like a WH-question's "?".
  const hug = after.match(/^[?.!,;:]+/)?.[0] ?? ''
  const split = splitAround(before, item.answer, after.slice(hug.length))
  const imperative = item.family === 'imperative'
  const stemChangeVerb = verbs.find((verb) => verb.infinitive === baseVerb(item.verb) && verb.stemChange)
  return {
    topic: 'conjugation',
    badge: imperative ? 'Presente do subjuntivo · imperativo' : (DRILL_TENSES.find((entry) => entry.id === 'presentSubjunctive')?.badge ?? ''),
    cueText: item.verb,
    answer: item.answer,
    translation: item.en,
    answerAriaLabel: `Conjugate ${item.verb}`,
    disclaimer: imperative ? 'Commands with você — and every "não …" command — use the present subjunctive form.' : '',
    suffix: hug || undefined,
    stemChangeVerb,
    beforeWords: split.beforeWords,
    afterWords: split.afterWords,
    sentence: `${before}${item.answer}${after}`,
  }
}

function buildSession(settings: Settings): DrillItem[] {
  const { conjugation, natural } = drillPool(settings)

  const pick = createPhrasePicker()

  const entries = [
    ...conjugation.map((poolItem) => ({ kind: 'conjugation' as const, poolItem })),
    ...natural.map((item) => ({ kind: 'natural' as const, item })),
  ]

  return shuffle(entries)
    .slice(0, Math.min(10, entries.length))
    .map((entry) => {
      if (entry.kind === 'natural') return buildNaturalItem(entry.item)
      const { verb, tense, pronoun, via } = entry.poolItem
      // "A gente" is shown as its own subject but conjugates with você's column.
      const subjects = PT_SUBJECTS[via]
      const subject = subjects[Math.floor(Math.random() * subjects.length)]
      const conjugated = verb.conjugations[tense][pronoun] ?? ''

      const frames = framesForVerb(verb)
      if (frames.length > 0 && Math.random() < QUESTION_SHARE) {
        return buildQuestionItem(verb, tense, subject, conjugated, frames)
      }
      return buildStatementItem(verb, tense, subject, conjugated, pick)
    })
}

function buildContractionItems(selectedIds: string[], hint: HintLevel): DrillItem[] {
  return buildContractionSession(selectedIds, hint).map((prompt) => ({
    topic: 'contraction',
    badge: 'Contração',
    beforeWords: prompt.beforeWords,
    afterWords: prompt.afterWords,
    sentence: prompt.sentence,
    cueText: prompt.cueText,
    answer: prompt.item.answer,
    translation: prompt.item.translation,
    answerAriaLabel: 'Type the contraction',
    disclaimer: '',
    answerNote: (typed) => possessionNote(prompt.item, typed),
  }))
}

function buildObjectPronounItems(
  categoryIds: OPCategory[],
  register: Register,
  placementIds: Placement[],
): DrillItem[] {
  return buildObjectPronounSession(categoryIds, register, placementIds).map((prompt) => {
    const { item } = prompt
    const canonical = prompt.canonicalAnswer
    return {
      topic: 'objectPronoun',
      badge: register === 'written' ? 'Pronome · escrita' : 'Pronome · fala',
      beforeWords: prompt.beforeWords,
      afterWords: prompt.afterWords,
      promptLines: prompt.promptLines,
      // The blank is the verb + pronoun cluster; the cue names the base verb and
      // the object ("encontrar + a Maria"), the same way for every person.
      cueText: prompt.cue,
      // Sentence-initial answers are shown with their capital: "Me ajuda".
      answer: item.before ? canonical : canonical.charAt(0).toLocaleUpperCase('pt') + canonical.slice(1),
      acceptedAnswers: prompt.acceptedAnswers,
      clusterOf: (typed) => clusterOf(item, typed),
      sentenceFor: (reply) => sentenceWith(item, reply),
      suffix: prompt.suffix || undefined,
      sentence: sentenceWith(item, canonical),
      translation: item.translation,
      answerAriaLabel: 'Type the verb and pronoun',
      disclaimer: '',
      contrast: prompt.contrast,
    }
  })
}

const COMPARATIVE_DISCLAIMER = 'Example sentences are generated to practice comparisons and may not always make logical sense.'

function buildComparativeItems(categoryIds: ComparativeCategory[], formIds: ComparativeForm[]): DrillItem[] {
  return buildComparativeSession(categoryIds, formIds).map((prompt) => ({
    topic: 'comparative',
    badge: 'Comparação',
    beforeWords: [],
    afterWords: [],
    promptLines: prompt.promptLines,
    cueText: '',
    answer: prompt.core,
    acceptedAnswers: prompt.acceptedCores,
    // No visual suffix: a trailing "." rendered as its own flex child could
    // get orphaned onto its own line when a long corrected sentence wraps.
    // The sentence spoken aloud still ends in a period, just not shown here.
    sentence: `${prompt.core}.`,
    translation: prompt.translation,
    answerAriaLabel: 'Type the Portuguese sentence',
    disclaimer: COMPARATIVE_DISCLAIMER,
    vocabHints: prompt.vocabHints,
  }))
}

function buildPluralItems(cardIds: PluralCardId[]): DrillItem[] {
  return buildPluralsSession(cardIds).map((prompt) => ({
    topic: 'plurals',
    badge: 'Plural',
    beforeWords: prompt.beforeWords,
    afterWords: [],
    cueText: '',
    answer: prompt.answer,
    sentence: prompt.spoken,
    translation: '',
    answerAriaLabel: 'Type the plural',
    disclaimer: '',
    tip: prompt.tip,
    tipRows: prompt.tipRows ?? undefined,
    speaksAnswerOnly: true,
  }))
}

// ---------------------------------------------------------------------------
// Speech
// ---------------------------------------------------------------------------

const FEMALE_VOICE_NAMES = /francisca|luciana|fernanda|letícia|leticia|maria|th[áa]lita|bruna|camila|giovanna|isabela|mariana|vit[óo]ria|yara|google português|female|woman/i
const MALE_VOICE_NAMES = /antonio|antônio|cristiano|duarte|felipe|ricardo|male|man/i
const CLEAR_VOICE_NAMES = /natural|premium|enhanced|online|google/i

function preferredPortugueseVoice() {
  return window.speechSynthesis
    .getVoices()
    .filter((voice) => /^pt[-_]br$/i.test(voice.lang))
    .sort((left, right) => voiceScore(right) - voiceScore(left))[0]
}

function voiceScore(voice: SpeechSynthesisVoice) {
  let score = 0
  if (FEMALE_VOICE_NAMES.test(voice.name)) score += 100
  if (CLEAR_VOICE_NAMES.test(voice.name)) score += 50
  if (!voice.localService) score += 10
  if (MALE_VOICE_NAMES.test(voice.name)) score -= 100
  return score
}

// Returns true when speech actually started, so callers can tell whether to
// expect the onEnd callback (fired on the utterance's `end` or `error`).
// onBoundary reports the character offset of each word as it is spoken.
function speak(text: string, muted: boolean, onEnd?: () => void, onBoundary?: (charIndex: number) => void) {
  if (muted || !('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) return false

  window.speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = 'pt-BR'
  utterance.rate = 0.92
  const voice = preferredPortugueseVoice()
  if (voice) utterance.voice = voice
  if (onEnd) {
    utterance.addEventListener('end', onEnd)
    utterance.addEventListener('error', onEnd)
  }
  if (onBoundary) utterance.addEventListener('boundary', (event) => onBoundary(event.charIndex))
  window.speechSynthesis.speak(utterance)
  return true
}

// A near-silent 0.1s WAV clip, played once per page load purely to "unlock"
// the audio session on iOS Safari before the first speechSynthesis call.
// Without this, iOS often routes Web Speech API audio to the phone's own
// speaker even when Bluetooth headphones are connected and handling
// everything else on the device — playing a real <audio> element first, tied
// to a genuine tap, nudges Safari into treating subsequent audio (including
// speech) as ordinary media playback that follows the active Bluetooth route.
const SILENT_AUDIO_SRC =
  'data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAoA8AAKAPAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA'

let audioSessionPrimed = false

// Called from a real tap handler (never from an effect) so the browser still
// counts it as a direct response to user input.
function primeAudioSessionForBluetooth() {
  if (audioSessionPrimed) return
  audioSessionPrimed = true
  new Audio(SILENT_AUDIO_SRC).play().catch(() => {})
}

// Pronounce one conjugated form aloud on an explicit tap. speak() already
// cancels any in-progress utterance, so only one clip ever plays at a time.
// Audio uses the browser's on-device pt-BR speech synthesis (zero latency /
// cost). To swap in pre-generated files later, drop them at
// public/audio/pron/<form>.mp3 — lowercase, NFC accents kept, spaces → "-" —
// keyed by the conjugated string so identical forms across verbs share one file.
function pronounceForm(text: string, onEnd: () => void) {
  if (!speak(text, false, onEnd)) onEnd()
}

function SpeakerWaveIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06Z" />
      <path d="M15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.061 4.5 4.5 0 0 0 0-6.364.75.75 0 0 1 0-1.06Z" />
    </svg>
  )
}

function SpeakerWaveOutlineIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
    </svg>
  )
}

function SpeakerOffIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
    </svg>
  )
}

function LightBulbIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  )
}

// Shared with the Songs tab's gold circle icon, so the nav glyph and the
// song-card icon are the same mark.
const MUSIC_NOTE_PATH =
  'M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z'

function MusicNoteIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d={MUSIC_NOTE_PATH} />
    </svg>
  )
}

const CHEVRON_PATHS = {
  up: 'm4.5 15.75 7.5-7.5 7.5 7.5',
  down: 'm19.5 8.25-7.5 7.5-7.5-7.5',
  left: 'm15.75 19.5-7.5-7.5 7.5-7.5',
  right: 'm8.25 4.5 7.5 7.5-7.5 7.5',
} as const

function ChevronIcon({ direction, className }: { direction: keyof typeof CHEVRON_PATHS; className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d={CHEVRON_PATHS[direction]} />
    </svg>
  )
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  )
}

function PlayCircleIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  )
}

// Nav glyphs for the main sidebar/bottom-nav sections — one small outline
// icon per section, same visual family as the icons above.
function NavGlyph({ id, className }: { id: 'drills' | 'reference' | 'vocabulary' | 'translate' | 'songs' | 'about'; className?: string }) {
  const paths: Record<typeof id, string> = {
    drills: 'M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z',
    reference:
      'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25',
    vocabulary:
      'M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122',
    translate:
      'm10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802',
    songs: MUSIC_NOTE_PATH,
    // Lowercase "j" monogram in a circle — jeyfala's own mark, not a generic
    // info icon, kept in the same stroke weight as the other nav glyphs.
    about: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M12.75 7.5h.008v.008h-.008V7.5z M12.75 10.5v4.5a2.25 2.25 0 01-2.25 2.25h-.75',
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d={paths[id]} />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" className={className}>
      <rect x="3.75" y="3.75" width="16.5" height="16.5" rx="4.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="16.5" cy="7.5" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  )
}

// The plain colour key + tap-to-hear hint. A display note, not a filter — it
// rides on the subtitle line (right-aligned) rather than in the filter stack.
function TableLegend({ swatch }: { swatch?: 'regular' | 'both' }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
      {swatch && (
        <span className="inline-flex items-center gap-1.5">
          <Swatch className="bg-blue-700" /> regular ending
        </span>
      )}
      {swatch === 'both' && (
        <span className="inline-flex items-center gap-1.5">
          <Swatch className="bg-amber-600" /> irregular form
        </span>
      )}
      <span className="inline-flex items-center gap-1">
        <SpeakerWaveOutlineIcon className="size-3.5 text-blue-600" /> tap to hear it
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Setup screen
// ---------------------------------------------------------------------------

function Setup({
  settings,
  setSettings,
  onStart,
  complete = false,
  score = null,
}: {
  settings: Settings
  setSettings: (settings: Settings) => void
  onStart: () => void
  complete?: boolean
  score?: DrillScore | null
}) {
  const { conjugation, natural } = drillPool(settings)
  const available = new Set([...conjugation.map((item) => item.verb.infinitive), ...natural.map((item) => baseVerb(item.verb))]).size
  const promptCount = conjugation.length + natural.length
  const valid = settings.tenses.length > 0 && settings.verbTypes.length > 0 && settings.pronouns.length > 0 && promptCount > 0

  return (
    <>
      {complete && (
        <div className="mt-3 mb-8 rounded-2xl bg-emerald-50 px-5 py-4 text-center ring-1 ring-emerald-200">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">
            Session complete{score && ` — ${score.correct} of ${score.total}`}
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            Practice Again
          </button>
          <p className="mt-1 text-xs text-slate-500">or adjust settings below</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-950">Conjugation drill</h2>
          <p aria-live="polite" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
            {available} verbs · {promptCount} possible prompts
          </p>
        </div>

        <fieldset className="border-t border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Tenses</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {DRILL_TENSES.map((tense) => (
              <Choice key={tense.id} chip checked={settings.tenses.includes(tense.id)} label={tense.label} onChange={() => setSettings({ ...settings, tenses: toggleValue(settings.tenses, tense.id) })} />
            ))}
          </div>
        </fieldset>

        <fieldset className="border-t border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Verb types</legend>
          {showStemChangeHint(settings) && (
            <p className="mt-1 text-xs text-slate-500">Stem changes mostly appear in the present tense — verbs may conjugate regularly in other tenses.</p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {VERB_TYPES.map((type) => (
              <Choice key={type.id} chip checked={settings.verbTypes.includes(type.id)} label={type.label} onChange={() => setSettings({ ...settings, verbTypes: toggleValue(settings.verbTypes, type.id) })} />
            ))}
          </div>
        </fieldset>

        <fieldset className="border-y border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Pronouns</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {DRILL_PRONOUNS.map((pronoun) => (
              <Choice key={pronoun.id} chip checked={settings.pronouns.includes(pronoun.id)} label={<span translate="no">{pronoun.label}</span>} onChange={() => setSettings({ ...settings, pronouns: toggleValue(settings.pronouns, pronoun.id) })} />
            ))}
          </div>
        </fieldset>

        {!valid && (
          <p role="alert" className="mt-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {settings.tenses.length > 0 && settings.verbTypes.length > 0 && settings.pronouns.length > 0
              ? 'No sentences match these settings — try more verb types or pronouns.'
              : 'Choose at least one tense, verb type, and pronoun.'}
          </p>
        )}
        <button type="button" disabled={!valid} onClick={onStart} className="mt-4 w-full rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300">
          {complete ? 'Start another drill' : `Start ${Math.min(10, promptCount)}-question drill`}
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Contractions — drill setup + reference table
// ---------------------------------------------------------------------------

// Chosen once per session; controls how much of the preposition + article
// breakdown is shown beside the blank. Ordered easiest → hardest.
const HINT_LEVELS: Array<{ id: HintLevel; label: string; example: string }> = [
  { id: 'full', label: 'Guided', example: 'shows (em + a)' },
  { id: 'prep', label: 'Semi-guided', example: 'shows (em + ___)' },
  { id: 'none', label: 'Self-guided', example: 'no parenthetical' },
]

function RadioChoice({ checked, label, name, onChange }: { checked: boolean; label: ReactNode; name: string; onChange: () => void }) {
  return (
    <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2 text-sm font-medium leading-tight transition-colors ${checked ? 'border-purple-300 bg-purple-50 text-purple-950' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="size-4 shrink-0 border-slate-300 text-purple-700 focus:ring-purple-600" />
      {label}
    </label>
  )
}

const CONTRACTION_GROUPS: Array<{ id: ContractionGroup; legend: string }> = [
  { id: 'article', legend: 'Preposition + article' },
  { id: 'pronoun', legend: 'Preposition + pronoun' },
]

function ContractionSetup({
  ids,
  setIds,
  hint,
  setHint,
  onStart,
  complete = false,
  score = null,
}: {
  ids: string[]
  setIds: (ids: string[]) => void
  hint: HintLevel
  setHint: (hint: HintLevel) => void
  onStart: () => void
  complete?: boolean
  score?: DrillScore | null
}) {
  const promptCount = contractionPromptCount(ids)
  const valid = ids.length > 0
  // Checking a card in one group clears the other (they run on different practice formats).
  const pronounOnly = onlyPronounCards(ids)
  const toggleCard = (id: string) => setIds(toggleContractionCard(ids, id))

  return (
    <>
      {complete && (
        <div className="mt-3 mb-8 rounded-2xl bg-emerald-50 px-5 py-4 text-center ring-1 ring-emerald-200">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">
            Session complete{score && ` — ${score.correct} of ${score.total}`}
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            Practice Again
          </button>
          <p className="mt-1 text-xs text-slate-500">or adjust settings below</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-950">Contraction drill</h2>
          <p aria-live="polite" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
            {promptCount} possible prompts
          </p>
        </div>

        {CONTRACTION_GROUPS.map((group) => (
          <fieldset key={group.id} className="border-t border-slate-200 py-4">
            <legend className="text-lg font-semibold text-slate-700">{group.legend}</legend>
            <div className="mt-2 grid w-full gap-2 sm:grid-cols-2">
              {CONTRACTION_CATEGORIES.filter((category) => category.group === group.id).map((category) => (
                <Choice
                  key={category.id}
                  checked={ids.includes(category.id)}
                  onChange={() => toggleCard(category.id)}
                  label={
                    <span className="flex flex-col leading-tight">
                      <span>
                        <span translate="no">{category.label}</span> <span translate="no" className="text-xs font-normal text-slate-400">({category.forms})</span>
                      </span>
                      <span className="mt-0.5 text-[10px] font-normal leading-tight text-slate-400">
                        {contractionSubtypes(category.id).join(' · ')}
                      </span>
                    </span>
                  }
                />
              ))}
            </div>
          </fieldset>
        ))}

        {pronounOnly ? (
          // The challenge level only governs the article cards; the pronoun cards always name the referent.
          <div className="border-t border-slate-200" />
        ) : (
          <fieldset className="border-y border-slate-200 py-4">
            <legend className="text-lg font-semibold text-slate-700">Challenge level</legend>
            <div className="mt-2 grid w-full gap-2 sm:grid-cols-3">
              {HINT_LEVELS.map((level) => (
                <RadioChoice
                  key={level.id}
                  name="contraction-hint"
                  checked={hint === level.id}
                  onChange={() => setHint(level.id)}
                  label={
                    <span className="flex flex-col leading-tight">
                      <span>{level.label}</span>
                      <span className="mt-0.5 text-[11px] font-normal text-slate-400">{level.example}</span>
                    </span>
                  }
                />
              ))}
            </div>
          </fieldset>
        )}

        {!valid && <p role="alert" className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">Choose at least one preposition.</p>}
        <button type="button" disabled={!valid} onClick={onStart} className="mt-5 w-full rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300">
          {complete ? 'Start another drill' : 'Start 10-question drill'}
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Object pronouns — drill setup (Practice tab only, no reference table yet)
// ---------------------------------------------------------------------------

function ObjectPronounSetup({
  register,
  setRegister,
  categories,
  setCategories,
  placements,
  setPlacements,
  onStart,
  complete = false,
  score = null,
}: {
  register: Register
  setRegister: (register: Register) => void
  categories: OPCategory[]
  setCategories: (categories: OPCategory[]) => void
  placements: Placement[]
  setPlacements: (placements: Placement[]) => void
  onStart: () => void
  complete?: boolean
  score?: DrillScore | null
}) {
  const promptCount = opPool(categories, register, placements).length
  const valid = categories.length > 0 && promptCount > 0

  return (
    <>
      {complete && (
        <div className="mt-3 mb-8 rounded-2xl bg-emerald-50 px-5 py-4 text-center ring-1 ring-emerald-200">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">
            Session complete{score && ` — ${score.correct} of ${score.total}`}
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            Practice Again
          </button>
          <p className="mt-1 text-xs text-slate-500">or adjust settings below</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-950">Object pronoun drill</h2>
          <p aria-live="polite" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
            {promptCount} possible prompts
          </p>
        </div>

        <fieldset className="border-t border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Style</legend>
          <div className="mt-2">
            <SegmentedControl ariaLabel="Style" options={OP_REGISTERS} value={register} onChange={setRegister} />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            <span className="font-medium text-slate-600">Casual</span> covers everyday talk and texting.{' '}
            <span className="font-medium text-slate-600">Formal</span> covers formal speech and writing.
          </p>
        </fieldset>

        <fieldset className="border-t border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Pronoun type</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {OP_CATEGORIES.map((category) => (
              <Choice
                key={category.id}
                chip
                checked={categories.includes(category.id)}
                onChange={() => setCategories(toggleValue(categories, category.id))}
                label={
                  <span>
                    {category.label}{' '}
                    <span translate="no" className="text-xs font-normal text-slate-400">
                      ({category.description})
                    </span>
                  </span>
                }
              />
            ))}
          </div>
        </fieldset>

        {register === 'written' && (
          <fieldset className="border-t border-slate-200 py-4">
            <legend className="text-lg font-semibold text-slate-700">Placement</legend>
            <div className="mt-2 grid w-full gap-2 sm:grid-cols-2">
              {OP_PLACEMENTS.map((placement) => (
                <Choice
                  key={placement.id}
                  checked={placements.includes(placement.id)}
                  onChange={() => setPlacements(toggleValue(placements, placement.id))}
                  label={
                    <span className="flex flex-col leading-tight">
                      <span>{placement.label}</span>
                      <span className="mt-0.5 text-[10px] font-normal leading-tight text-slate-400">{placement.cases}</span>
                    </span>
                  }
                />
              ))}
            </div>
          </fieldset>
        )}

        <div className="border-t border-slate-200 pt-4">
          {!valid && (
            <p role="alert" className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {categories.length === 0
                ? 'Choose at least one pronoun type.'
                : 'No prompts match — pick a placement above.'}
            </p>
          )}
          <button
            type="button"
            disabled={!valid}
            onClick={onStart}
            className="mt-4 w-full rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {complete ? 'Start another drill' : `Start ${Math.min(10, promptCount)}-question drill`}
          </button>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Comparisons — drill setup (Practice tab only, no fixed prompt pool since
// items are generated fresh each session)
// ---------------------------------------------------------------------------

function ComparativeSetup({
  categories,
  setCategories,
  forms,
  setForms,
  onStart,
  complete = false,
  score = null,
}: {
  categories: ComparativeCategory[]
  setCategories: (categories: ComparativeCategory[]) => void
  forms: ComparativeForm[]
  setForms: (forms: ComparativeForm[]) => void
  onStart: () => void
  complete?: boolean
  score?: DrillScore | null
}) {
  const patternCount = comparativePatternCount(categories, forms)
  const valid = categories.length > 0 && forms.length > 0 && patternCount > 0

  return (
    <>
      {complete && (
        <div className="mt-3 mb-8 rounded-2xl bg-emerald-50 px-5 py-4 text-center ring-1 ring-emerald-200">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">
            Session complete{score && ` — ${score.correct} of ${score.total}`}
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            Practice Again
          </button>
          <p className="mt-1 text-xs text-slate-500">or adjust settings below</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-950">Comparison drill</h2>
          <p aria-live="polite" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
            {patternCount} possible prompts
          </p>
        </div>

        <fieldset className="border-t border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Type</legend>
          <div className="mt-2 grid w-full gap-2 sm:grid-cols-3">
            {COMPARATIVE_CATEGORIES.map((category) => (
              <Choice
                key={category.id}
                checked={categories.includes(category.id)}
                onChange={() => setCategories(toggleValue(categories, category.id))}
                label={
                  <span className="flex flex-col leading-tight">
                    <span>{category.label}</span>
                    <span className="mt-0.5 text-[11px] font-normal leading-tight text-slate-400">{category.description}</span>
                  </span>
                }
              />
            ))}
          </div>
        </fieldset>

        <fieldset className="border-y border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Form</legend>
          <p className="mt-1 text-xs text-slate-500">Irregular applies to adjective and superlative comparisons. Verb-based comparison is all regular.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {COMPARATIVE_FORMS.map((form) => (
              <Choice
                key={form.id}
                chip
                checked={forms.includes(form.id)}
                onChange={() => setForms(toggleValue(forms, form.id))}
                label={form.label}
              />
            ))}
          </div>
        </fieldset>

        {!valid && (
          <p role="alert" className="mt-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {categories.length === 0
              ? 'Choose at least one type.'
              : forms.length === 0
                ? 'Choose at least one form.'
                : 'No prompts match this combination — pick a different type or form above.'}
          </p>
        )}
        <button type="button" disabled={!valid} onClick={onStart} className="mt-4 w-full rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300">
          {complete ? 'Start another drill' : `Start ${COMPARATIVE_SESSION_LENGTH}-question drill`}
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Plurals — drill setup (Practice tab). The cards run easiest first, and all start selected.
// ---------------------------------------------------------------------------

function PluralsSetup({
  ids,
  setIds,
  onStart,
  complete = false,
  score = null,
}: {
  ids: PluralCardId[]
  setIds: (ids: PluralCardId[]) => void
  onStart: () => void
  complete?: boolean
  score?: DrillScore | null
}) {
  const promptCount = pluralPromptCount(ids)
  const valid = ids.length > 0

  return (
    <>
      {complete && (
        <div className="mt-3 mb-8 rounded-2xl bg-emerald-50 px-5 py-4 text-center ring-1 ring-emerald-200">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">
            Session complete{score && ` — ${score.correct} of ${score.total}`}
          </p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            Practice Again
          </button>
          <p className="mt-1 text-xs text-slate-500">or adjust settings below</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6">
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-950">Plural drill</h2>
          <p aria-live="polite" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
            {promptCount} possible prompts
          </p>
        </div>

        <fieldset className="border-y border-slate-200 py-4">
          <legend className="text-lg font-semibold text-slate-700">Plural endings</legend>
          <div className="mt-2 grid w-full gap-2 sm:grid-cols-2">
            {PLURAL_CARDS.map((card) => (
              <Choice
                key={card.id}
                checked={ids.includes(card.id)}
                onChange={() => setIds(togglePluralCard(ids, card.id))}
                label={
                  <span className="flex flex-col leading-tight">
                    <span>
                      <span translate="no">{card.label}</span>{' '}
                      <span translate="no" className="text-xs font-normal text-slate-400">({card.examples})</span>
                    </span>
                    {card.note && <span className="mt-0.5 text-[10px] font-normal leading-tight text-slate-400">{card.note}</span>}
                  </span>
                }
              />
            ))}
          </div>
        </fieldset>

        {!valid && <p role="alert" className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">Choose at least one card.</p>}
        <button type="button" disabled={!valid} onClick={onStart} className="mt-5 w-full rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300">
          {complete ? 'Start another drill' : `Start ${PLURALS_SESSION_LENGTH}-question drill`}
        </button>
      </div>
    </>
  )
}

// Small colour key square, used in both reference legends instead of spelling
// the colour name out.
function Swatch({ className }: { className: string }) {
  return <span aria-hidden="true" className={`inline-block size-3 shrink-0 rounded-sm ${className}`} />
}

// Prepositions whose contraction is casual (pro / pra), flagged beside the gloss with the same
// small CASUAL caption the object-pronoun tables use, so it reads as a register label and not
// as part of the English meaning.
const CASUAL_PREPS = new Set(['para'])

function prepGloss(prep: string): ReactNode {
  return (
    <>
      {PREP_GLOSS[prep]}
      {CASUAL_PREPS.has(prep) && (
        <>
          {' '}
          <span aria-hidden="true">·</span> <RegisterTag register="casual" />
        </>
      )}
    </>
  )
}

// Most foundational sense of each preposition — deliberately not exhaustive.
const PREP_GLOSS: Record<string, string> = {
  de: 'of / from',
  em: 'in / on',
  a: 'to / at',
  por: 'by / through',
  para: 'to / for',
  com: 'with',
}

// Quick-glance cheat sheet: when a preposition is called for (the part the
// contraction table can't show). One short line each — topic and rule.
type UsageNote = { pattern: string; rule: string }

// Notes on the article table: they sit directly under it, in the same card. A rule is one short
// line, with a tiny example in brackets where the pattern needs one.
const ARTICLE_USAGE: UsageNote[] = [
  {
    pattern: 'para → pra / pro',
    rule: '"ir a" = a short trip, "ir para" = a longer stay; casual speech uses pra for both',
  },
  {
    pattern: 'o / a',
    rule: "before general places, abstract ideas, countries and rivers (as praias, o Brasil); optional before a person's name (casual: keep, formal: drop)",
  },
  {
    pattern: 'article + possessive',
    rule: 'the article goes before a possessive (a minha casa); casual speech can drop it',
  },
  {
    pattern: 'time of day',
    rule: 'de manhã · à tarde · à noite',
  },
  {
    pattern: 'referencing days',
    rule: 'na = one specific day · às = recurring · aos for weekends',
  },
  {
    pattern: 'transportation method',
    rule: 'de + vehicle (de carro, de ônibus); walking is "a pé"',
  },
]

// Set phrases that always take a given preposition (not tied to one table): the "Common phrases" card.
const CONTRACTION_USAGE: UsageNote[] = [
  {
    pattern: 'verb + preposition',
    rule: 'gostar de (like) · precisar de (need) · pensar em (think about) · falar de/sobre (talk about) · lembrar de (remember) · esquecer de (forget) · acreditar em (believe in) · depender de (depend on) · sonhar com (dream about) · casar com (marry) · interessar-se por (take an interest in)',
  },
  {
    pattern: 'place/time prepositions',
    rule: 'antes de (before) · depois de (after) · perto de (near) · longe de (far) · na frente de (in front) · atrás de (behind) · ao lado de (next to) · dentro de (inside) · fora de (outside) · em cima de (on top) · embaixo de (under) · no meio de (in middle) · ao redor de (around)',
  },
]

// Preposition + pronoun (Grammar tab): when each form is called for, most common use first, and
// why dela is clearer than seu/sua. Only de and em contract with a pronoun, so the table has no
// a / por / para rows. **bold** marks the contracted word.
const PRONOUN_USAGE: UsageNote[] = [
  {
    pattern: 'possession (de)',
    rule: 'dele / dela / deles / delas go after the noun: o nome **dela**, a casa **deles**',
  },
  {
    pattern: 'verb + de',
    rule: 'Preciso **dela** para terminar. · Gosto **dele**.',
  },
  {
    pattern: 'verb + em',
    rule: 'Penso **nela** todos os dias. · Você confia **nele**?',
  },
  {
    pattern: 'dela vs. seu/sua',
    rule: 'seu/sua can mean your, his, her, or their. Use dele/dela to be clear whose it is.',
  },
  {
    pattern: 'agreement',
    rule: 'dele/dela/deles/delas agree with the possessor (o nome **dela**, a casa **delas**); seu/sua agree with the thing possessed (**seu** nome, **sua** casa).',
  },
]

// com + pronoun (Grammar tab), most common first. The two-word forms are a gloss list: the grid reads
// "com ele (with him) · …". **bold** marks the merged word.
const COM_PRONOUN_USAGE: UsageNote[] = [
  { pattern: 'comigo', rule: 'always one word, never "com mim"' },
  { pattern: 'with you', rule: '**com você** is the everyday choice; **contigo** goes with tu' },
  { pattern: 'with us', rule: '**com a gente** is what people say; **conosco** is written or careful' },
  {
    pattern: 'two words',
    rule: COM_TWO_WORD_FORMS.map(([form, gloss]) => `${form} (${gloss})`).join(' · '),
  },
  { pattern: 'consigo', rule: 'points back to the subject (Ele leva dinheiro **consigo**), not the verb consigo (Não **consigo** abrir)' },
]

// The columns of the pronoun table: ele / ela / eles / elas, each with its de and em form.
// ele / ela also stand for things (nela = in / on it), so the gloss says "it" too.
const PRONOUN_COLUMN_GLOSS: Record<string, string> = { ele: 'he / it', ela: 'she / it', eles: 'they (m.)', elas: 'they (f.)' }

function pronounColumns(): ContractionColumn[] {
  return Object.keys(PRONOUN_CONTRACTIONS).map((pronoun) => ({
    key: pronoun,
    headerWord: pronoun,
    headerSub: PRONOUN_COLUMN_GLOSS[pronoun],
    cell: (prep) => ({ word: PRONOUN_CONTRACTIONS[pronoun][prep] }),
  }))
}

// The four com + pronoun columns: the pronoun on top, the merged form under com.
function comPronounColumns(): ContractionColumn[] {
  return COM_PRONOUN_FORMS.map(({ pronoun, pair, gloss, form }) => ({
    key: `com:${pronoun}`,
    headerWord: pronoun,
    headerSub: gloss,
    headerPair: pair ? { word: pair, playKey: `col:${pair}` } : undefined,
    cell: () => ({ word: form }),
  }))
}

// A Topic / Rule table of usage notes. A rule that is a list of phrases,
// each with its meaning, becomes a grid you can scan instead of a run-on line.
function UsageTable({ notes, topic, detail }: { notes: UsageNote[]; topic: string; detail: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[30rem] text-left text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="w-52 px-4 py-2.5 font-semibold">{topic}</th>
            <th className="px-4 py-2.5 font-semibold">{detail}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {notes.map((note) => (
            <tr key={note.pattern}>
              <th scope="row" translate="no" className="whitespace-nowrap px-4 py-2 text-left font-semibold text-slate-800">{note.pattern}</th>
              <td className="px-4 py-2 text-slate-600">
                {glossList(note.rule) ? (
                  <ul className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-x-5 gap-y-1">
                    {glossList(note.rule)!.map(([phrase, gloss]) => (
                      <li key={phrase}>
                        <span translate="no" className="font-medium text-slate-700">{phrase}</span>{' '}
                        <span className="text-xs text-slate-400">{gloss}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  boldPattern(note.rule)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// The title of a reference card, with its one-line description beside it (it drops
// under the title only when the line is too long) instead of always on its own line.
function CardHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-slate-200 px-5 py-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {children && <p className="text-sm text-slate-500">{children}</p>}
    </div>
  )
}

// "perto de (near) · longe de (far) · …" → [["perto de", "near"], …], or null when the text
// isn't such a list (every part needs a "(gloss)"), so ordinary sentences stay as they are.
function glossList(rule: string): Array<[string, string]> | null {
  const parts = rule.split(' · ')
  if (parts.length < 4) return null
  const items: Array<[string, string]> = []
  for (const part of parts) {
    const match = part.match(/^(.+?) \((.+)\)$/)
    if (!match) return null
    items.push([match[1], match[2]])
  }
  return items
}

// A speak-on-tap word cell for the contractions table. `sub` stacks a small
// grey line under the word (gender/number or a gloss) for the header cells.
// `pair` folds a masculine/feminine pair (este/esta) into one line written as
// "este/a": the masculine word, then the feminine ending as its own tap target
// (it speaks the whole feminine word). `compact` tightens the padding.
function ContractionCell({
  as,
  word,
  sub,
  pair,
  tone = 'contraction',
  compact = false,
  playKey,
  playingKey,
  onPlay,
}: {
  as: 'th-col' | 'th-row' | 'td'
  word: string
  sub?: ReactNode
  pair?: { word: string; playKey: string }
  tone?: 'contraction' | 'twoWord' | 'header'
  compact?: boolean
  playKey: string
  playingKey: string | null
  onPlay: (key: string, text: string) => void
}) {
  const wordClass = tone === 'twoWord' ? 'text-slate-500' : tone === 'header' ? 'font-semibold text-slate-800' : 'font-semibold text-blue-700'
  const wordButton = (forWord: string, forKey: string, icon = true) => {
    const playing = playingKey === forKey
    return (
      <button
        type="button"
        translate="no"
        onClick={() => onPlay(forKey, forWord)}
        aria-label={`Play pronunciation of ${forWord}`}
        className={`group flex items-center gap-1.5 whitespace-nowrap rounded transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-blue-50 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-300 ${playing ? 'bg-blue-50' : ''}`}
      >
        <span className={wordClass}>{forWord}</span>
        {icon && (
          <SpeakerWaveOutlineIcon
            className={`size-3.5 shrink-0 text-blue-600 transition-all duration-150 ${playing ? 'scale-100 opacity-100' : 'scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100'}`}
          />
        )}
      </button>
    )
  }
  // The feminine ending: what is left of "esta" once the shared stem "est" is
  // taken off — shown as "/a" ("estas" → "/as", "àquela" → "/a").
  const pairButton = (forPair: { word: string; playKey: string }) => {
    const ending = pairEnding(word, forPair.word)
    const playing = playingKey === forPair.playKey
    return (
      <button
        type="button"
        translate="no"
        onClick={() => onPlay(forPair.playKey, forPair.word)}
        aria-label={`Play pronunciation of ${forPair.word}`}
        className={`rounded transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-blue-50 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-300 ${playing ? 'bg-blue-50' : ''}`}
      >
        <span className="text-slate-300">/</span>
        <span className={wordClass}>{ending}</span>
      </button>
    )
  }
  const content = (
    // Compact grids put a header's small grey gloss on the same line ("de  of / from"), not under it.
    <div className={`flex w-full text-left ${compact ? 'flex-wrap items-baseline gap-x-1.5 px-3 py-2' : 'flex-col items-start gap-0.5 px-4 py-3'}`}>
      {pair ? (
        <span className="inline-flex items-center">
          {wordButton(word, playKey, false)}
          {pairButton(pair)}
        </span>
      ) : as === 'th-row' && compact ? (
        // A fixed-width label column, so every row's gloss starts in the same place.
        <span className="inline-block min-w-[2.5rem]">{wordButton(word, playKey, false)}</span>
      ) : (
        wordButton(word, playKey)
      )}
      {sub && <span className="text-xs font-normal text-slate-400">{sub}</span>}
    </div>
  )
  if (as === 'td') return <td className="p-0 align-top">{content}</td>
  return (
    <th scope={as === 'th-col' ? 'col' : 'row'} className="p-0 align-top">
      {content}
    </th>
  )
}

// One rendered column of the reference grid: a header (optionally a
// masculine/feminine pair sharing one column) plus a cell per preposition.
type ContractionColumn = {
  key: string
  headerWord: string
  headerSub: string
  headerPair?: { word: string; playKey: string }
  cell: (prep: string) => { word: string; pair?: { word: string; playKey: string } }
}

// The masculine/feminine pairs of the Articles table, in display order.
// Folding o+a into one column ("o/a", "do/a") is what halves its width.
const ARTICLE_PAIRS: Array<[masc: string, fem: string, genderLabel: string]> = [
  ['o', 'a', 'sing.'],
  ['os', 'as', 'pl.'],
  ['um', 'uma', 'sing.'],
  ['uns', 'umas', 'pl.'],
]

function articleColumns(rows: ContractionRow[]): ContractionColumn[] {
  const byArticle = new Map(rows.map((row) => [row.article, row]))
  return ARTICLE_PAIRS.map(([masc, fem, genderLabel]) => {
    const mascRow = byArticle.get(masc)!
    const femRow = byArticle.get(fem)!
    return {
      key: masc,
      headerWord: masc,
      headerSub: genderLabel,
      headerPair: { word: fem, playKey: `col:${fem}` },
      cell: (prep) => ({ word: mascRow.forms[prep], pair: { word: femRow.forms[prep], playKey: `cell:${prep}:${fem}` } }),
    }
  })
}

// How the feminine half of a folded pair is written after the "/":
//   este / esta → "/a"      do / da → "/a"       um / uma → "/a"   (de um / de uma → "/a")
//   uns / umas → "/umas"    ao / à → "/à"        de uns / de umas → "/umas"
// The ending alone when the feminine only swaps or adds a few final letters;
// otherwise the whole feminine word, minus any words the two share ("de ").
function pairEnding(masc: string, fem: string): string {
  if (fem.startsWith(masc)) return fem.slice(masc.length)
  const shared = commonPrefixLength(masc, fem)
  if (masc.length === fem.length && shared > 0 && fem.length - shared <= 2) return fem.slice(shared)
  const sharedWords = masc.split(' ').slice(0, -1).filter((word, index) => word === fem.split(' ')[index])
  return fem.split(' ').slice(sharedWords.length).join(' ')
}

// masc/fem/neuter triples for each demonstrative series, in display order.
// Folding este+esta into one column (instead of two) is what keeps this
// table from needing as many columns as the old combined one did.
const DEMONSTRATIVE_PAIRS: Array<[masc: string, fem: string | null, genderLabel: string]> = [
  ['este', 'esta', 'sing.'],
  ['estes', 'estas', 'pl.'],
  ['isto', null, 'neuter'],
  ['esse', 'essa', 'sing.'],
  ['esses', 'essas', 'pl.'],
  ['isso', null, 'neuter'],
  ['aquele', 'aquela', 'sing.'],
  ['aqueles', 'aquelas', 'pl.'],
  ['aquilo', null, 'neuter'],
]

function demonstrativeColumns(rows: ContractionRow[]): ContractionColumn[] {
  const byArticle = new Map(rows.map((row) => [row.article, row]))
  return DEMONSTRATIVE_PAIRS.map(([masc, fem, genderLabel]) => {
    const mascRow = byArticle.get(masc)!
    const femRow = fem ? byArticle.get(fem) : undefined
    return {
      key: masc,
      headerWord: masc,
      headerSub: genderLabel,
      headerPair: femRow ? { word: fem as string, playKey: `col:${fem}` } : undefined,
      cell: (prep) => ({
        word: mascRow.forms[prep],
        pair: femRow ? { word: femRow.forms[prep], playKey: `cell:${prep}:${fem}` } : undefined,
      }),
    }
  })
}

// One prep × column grid — factored out so the reference can show articles
// and demonstratives as two separate, appropriately-sized tables instead of
// one 23-column table that only scrolled to read. `preps` lets each table
// drop rows that are always empty for its columns (por/para never contract
// with a demonstrative).
function ContractionGrid({
  preps,
  columns,
  compact = false,
  glosses = true,
  playingKey,
  onPlay,
}: {
  preps: readonly string[]
  columns: ContractionColumn[]
  compact?: boolean
  // Show "of / from" under de, "in / on" under em… (the demonstratives table leaves them off).
  glosses?: boolean
  playingKey: string | null
  onPlay: (key: string, text: string) => void
}) {
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${compact ? 'min-w-[30rem]' : 'min-w-[36rem]'} text-left text-sm`}>
        <thead className="bg-slate-50 text-slate-600">
          <tr className="divide-x divide-slate-100">
            <th className={compact ? 'px-3 py-2' : 'px-4 py-3'} />
            {columns.map((column) => (
              <ContractionCell
                key={column.key}
                as="th-col"
                word={column.headerWord}
                sub={column.headerSub}
                pair={column.headerPair}
                tone="header"
                compact={compact}
                playKey={`col:${column.key}`}
                playingKey={playingKey}
                onPlay={onPlay}
              />
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {preps.map((prep) => (
            <tr key={prep} className="divide-x divide-slate-100">
              <ContractionCell as="th-row" word={prep} sub={glosses ? prepGloss(prep) : undefined} tone="header" compact={compact} playKey={`row:${prep}`} playingKey={playingKey} onPlay={onPlay} />
              {columns.map((column) => {
                const { word, pair } = column.cell(prep)
                if (word === '—' && (!pair || pair.word === '—')) {
                  return (
                    <td key={column.key} className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} text-slate-300`}>—</td>
                  )
                }
                return (
                  <ContractionCell
                    key={column.key}
                    as="td"
                    word={word}
                    pair={pair}
                    tone={word.includes(' ') ? 'twoWord' : 'contraction'}
                    compact={compact}
                    playKey={`cell:${prep}:${column.key}`}
                    playingKey={playingKey}
                    onPlay={onPlay}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ContractionReference() {
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const playForm = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }
  const articleRows = CONTRACTION_TABLE.filter((row) => row.kind !== 'demonstrative')
  const demonstrativeRows = CONTRACTION_TABLE.filter((row) => row.kind === 'demonstrative')

  return (
    <>
      <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Preposition + article" />
        <ContractionGrid preps={CONTRACTION_PREPS} columns={articleColumns(articleRows)} compact playingKey={playingKey} onPlay={playForm} />
        <div className="border-t border-slate-100">
          <UsageTable notes={ARTICLE_USAGE} topic="Topic" detail="Rule" />
        </div>
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Preposition + pronoun" />
        <ContractionGrid preps={['de', 'em']} columns={pronounColumns()} compact playingKey={playingKey} onPlay={playForm} />
        <div className="border-t border-slate-100">
          <UsageTable notes={PRONOUN_USAGE} topic="Topic" detail="Rule" />
        </div>
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="com + pronoun" />
        <ContractionGrid preps={['com']} columns={comPronounColumns()} compact playingKey={playingKey} onPlay={playForm} />
        <div className="border-t border-slate-100">
          <UsageTable notes={COM_PRONOUN_USAGE} topic="Topic" detail="Rule" />
        </div>
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Preposition + demonstrative" />
        <ContractionGrid preps={['de', 'em', 'a']} columns={demonstrativeColumns(demonstrativeRows)} compact glosses={false} playingKey={playingKey} onPlay={playForm} />
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Common phrases" />
        <UsageTable notes={CONTRACTION_USAGE} topic="Type" detail="Phrases" />
      </article>
    </>
  )
}

// ---------------------------------------------------------------------------
// Object pronouns — Grammar-tab reference (forms, register, placement)
// ---------------------------------------------------------------------------

// A cell in the forms table: `w` = the formal form(s) (also used in all
// speech); `s` = the casual-only equivalent(s), present for the 3rd person
// where the registers diverge. `sNote` is a small caveat shown after them.
type OPRefCell = { w: string[]; s?: string[]; sNote?: string }
type OPRefRow = { subject: string; en: string; direct: OPRefCell; indirect: OPRefCell; reflexive: OPRefCell }

// Casual 3rd-person direct object: use ele / ela only when it's a person;
// for a thing, drop the pronoun and echo the verb.
const OP_DIRECT_3P_NOTE = 'a person; for a thing, drop it'

const OP_REF_ROWS: OPRefRow[] = [
  { subject: 'eu', en: 'I', direct: { w: ['me'] }, indirect: { w: ['me'] }, reflexive: { w: ['me'] } },
  { subject: 'você / tu', en: 'you', direct: { w: ['te'] }, indirect: { w: ['te'] }, reflexive: { w: ['te'] } },
  {
    subject: 'ele / ela',
    en: 'he / she / it',
    direct: { w: ['o', 'a'], s: ['ele', 'ela'], sNote: OP_DIRECT_3P_NOTE },
    indirect: { w: ['lhe'], s: ['pra ele', 'pra ela'] },
    reflexive: { w: ['se'] },
  },
  { subject: 'nós', en: 'we', direct: { w: ['nos'] }, indirect: { w: ['nos'] }, reflexive: { w: ['nos'] } },
  {
    subject: 'eles / elas',
    en: 'they',
    direct: { w: ['os', 'as'], s: ['eles', 'elas'], sNote: OP_DIRECT_3P_NOTE },
    indirect: { w: ['lhes'], s: ['pra eles', 'pra elas'] },
    reflexive: { w: ['se'] },
  },
]

// 3rd-person examples, one list per function — the only person where the
// registers split. Each line is tagged with its register; the second casual
// line for the direct object shows the drop-and-echo-the-verb move.
type OPRefExampleLine = { register: 'formal' | 'casual'; text: string; note?: string }
const OP_REF_EXAMPLES: Record<'direct' | 'indirect' | 'reflexive', OPRefExampleLine[]> = {
  direct: [
    { register: 'formal', text: 'Encontrei-**a** na festa.' },
    { register: 'casual', text: 'Encontrei **ela** na festa.', note: 'a person' },
    { register: 'casual', text: 'Você comprou o pão? — Comprei.', note: 'a thing → drop it' },
  ],
  indirect: [
    { register: 'formal', text: 'Já **lhe** dei o recado.' },
    { register: 'casual', text: 'Já dei o recado **pra ela**.' },
  ],
  reflexive: [{ register: 'formal', text: 'Ela **se** arrumou rápido.' }],
}

const OP_REF_PLACEMENT: Array<{ where: string; when: string; example: string }> = [
  { where: 'After the verb — default', when: 'joined with a hyphen', example: 'Vi-**o** ontem no mercado. · Dá-**me** um minuto.' },
  { where: 'Before the verb — negation', when: 'não, nunca, ninguém, nada …', example: 'Não **o** vi. · Ninguém **me** avisou.' },
  { where: 'Before the verb — adverb', when: 'já, sempre, também, ainda …', example: 'Já **o** li. · Sempre **te** digo isso.' },
  { where: 'Before the verb — conjunction', when: 'que, porque, se, quando …', example: 'Sei que **a** viu. · Porque **o** esqueci em casa.' },
  { where: 'Before the verb — question word', when: 'quando, onde, por que, como …', example: 'Quando **o** viu? · Por que **me** ligou?' },
  { where: 'Before the verb — subordinate clause', when: 'inside a dependent clause', example: 'Quando **a** vi, sorri. · Assim que **o** abri, gostei.' },
]

// The small FORMAL / CASUAL caption on a register line. Colour matches the
// swatch in the table legend (blue = formal, grey = casual).
function RegisterTag({ register }: { register: 'formal' | 'casual' }) {
  return (
    <span
      className={`mr-1 text-[10px] font-semibold uppercase not-italic tracking-wide ${register === 'formal' ? 'text-blue-700' : 'text-slate-400'}`}
    >
      {register}
    </span>
  )
}

function OPRefFormCell({ cell, base, playingKey, onPlay }: { cell: OPRefCell; base: string; playingKey: string | null; onPlay: (key: string, text: string) => void }) {
  const forms = (list: string[], kind: 'w' | 's') =>
    list.map((form, index) => (
      <SpeakableWord
        key={`${base}:${kind}:${index}`}
        text={form}
        speakAs={speechForm(form)}
        muted={kind === 's'}
        playKey={`${base}:${kind}:${index}`}
        playingKey={playingKey}
        onPlay={onPlay}
      />
    ))
  // Only the 3rd-person cells split by register; there the two lines are
  // labelled FORMAL / CASUAL. Single-register cells (me, te, nos, se) stay bare.
  return (
    <td className="px-3 py-2 align-top">
      {/* FORMAL and CASUAL always share one line (the column widens to fit; the examples row carries the "a person / a thing" note). */}
      <div className="flex items-center gap-x-4 whitespace-nowrap">
        <span className="inline-flex items-center gap-x-0.5">
          {cell.s && <RegisterTag register="formal" />}
          {forms(cell.w, 'w')}
        </span>
        {cell.s && (
          <span className="inline-flex items-center gap-x-0.5">
            <RegisterTag register="casual" />
            {forms(cell.s, 's')}
          </span>
        )}
      </div>
    </td>
  )
}

function ObjectPronounReference() {
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const playForm = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }
  // "Before the verb — negation", "After the verb — default"… say the position once, as a group
  // heading (the same for both), and keep only what differs on each row.
  const placementRows: ReactNode[] = []
  let placementGroup: string | null = null
  for (const row of OP_REF_PLACEMENT) {
    const [lead, detail] = row.where.split(' — ')
    if (detail && placementGroup !== lead) {
      placementGroup = lead
      placementRows.push(
        // The group heading doubles as the column header for the rows under it.
        <tr key={`group:${lead}`} className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <th scope="colgroup" className="px-4 py-2 text-left">{lead}</th>
          <th scope="col" className="px-4 py-2 text-left">When</th>
          <th scope="col" className="px-4 py-2 text-left">Example</th>
        </tr>,
      )
    }
    placementRows.push(
      <tr key={row.where}>
        <th scope="row" className="whitespace-nowrap py-2 pl-8 pr-4 text-left font-semibold text-slate-800">{detail ?? row.where}</th>
        <td className="px-4 py-2 text-slate-600">{row.when}</td>
        <td translate="no" className="px-4 py-2 text-slate-600">{boldPattern(row.example)}</td>
      </tr>,
    )
  }
  const FUNCTIONS: Array<{ id: 'direct' | 'indirect' | 'reflexive'; label: string; sub: string }> = [
    { id: 'direct', label: 'Direct object', sub: 'what? / whom?' },
    { id: 'indirect', label: 'Indirect object', sub: 'to / for whom?' },
    { id: 'reflexive', label: 'Reflexive', sub: '-self' },
  ]

  return (
    <>
      <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="divide-x divide-slate-100">
                <th className="px-4 py-2.5 font-semibold">Person</th>
                {FUNCTIONS.map((fn) => (
                  <th key={fn.id} className="px-3 py-2.5 align-top">
                    <span className="font-semibold text-slate-800">{fn.label}</span>{' '}
                    <span className="text-xs font-normal text-slate-400">{fn.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {OP_REF_ROWS.map((row) => (
                <tr key={row.subject} className="divide-x divide-slate-100">
                  <th scope="row" className="whitespace-nowrap px-4 py-2 align-top text-left">
                    <span translate="no" className="font-semibold text-slate-800">{row.subject}</span>{' '}
                    <span className="text-xs font-normal text-slate-400">{row.en}</span>
                  </th>
                  {FUNCTIONS.map((fn) => (
                    <OPRefFormCell key={fn.id} cell={row[fn.id]} base={`${row.subject}:${fn.id}`} playingKey={playingKey} onPlay={playForm} />
                  ))}
                </tr>
              ))}
              <tr className="divide-x divide-slate-100 border-t-2 border-slate-200 bg-slate-50/60">
                <th scope="row" className="whitespace-nowrap px-4 py-2 align-top text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  examples
                </th>
                {FUNCTIONS.map((fn) => {
                  const lines = OP_REF_EXAMPLES[fn.id]
                  const labelled = lines.length > 1
                  return (
                    <td key={fn.id} className="px-3 py-2 align-top text-sm leading-snug">
                      {lines.map((line, index) => (
                        <p
                          key={index}
                          className={`${index > 0 ? 'mt-1 ' : ''}${line.register === 'formal' ? 'text-slate-600' : 'text-slate-500'}`}
                        >
                          {labelled && <RegisterTag register={line.register} />}
                          <span translate="no">{boldPattern(line.text)}</span>
                          {line.note && <span className="ml-1 text-[11px] text-slate-400">({line.note})</span>}
                        </p>
                      ))}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="bg-blue-700" /> <span className="font-semibold uppercase tracking-wide">formal</span> — formal writing + all speech
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="bg-slate-400" /> <span className="font-semibold uppercase tracking-wide">casual</span> — casual speech + texting
          </span>
        </div>
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Object Pronoun Placement">
            For the clitics only (<span translate="no">o / a / os / as, me / te / se, lhe / lhes</span>). The spoken <span translate="no" className="italic">ele / ela</span> and{' '}
            <span translate="no" className="italic">pra ele / ela</span> just follow the verb like any object — <span translate="no" className="italic">Não vi ele</span>.
          </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <tbody className="divide-y divide-slate-100">{placementRows}</tbody>
          </table>
        </div>
        <p className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
          In everyday talk and casual writing, Brazilians almost always put the pronoun before the verb (e.g. <span translate="no" className="italic">me viu</span>, <span translate="no" className="italic">te falei</span>) without the hyphen.
        </p>
      </article>
    </>
  )
}

// ---------------------------------------------------------------------------
// Plurals — Grammar-tab reference (cheat sheet only, no drill)
// ---------------------------------------------------------------------------

// One row of the plural-formation table: what the singular ends in, the change,
// and worked examples as [singular, plural] pairs.
type PluralRule = { ending: string; change: string; examples: Array<[string, string]> }

const PLURAL_RULES: PluralRule[] = [
  { ending: 'a vowel — a, e, i, o, u', change: 'add -s', examples: [['casa', 'casas'], ['livro', 'livros'], ['pé', 'pés']] },
  { ending: '-ão', change: 'usually → -ões', examples: [['coração', 'corações'], ['pão', 'pães'], ['mão', 'mãos']] },
  { ending: '-r, -z, -n', change: 'add -es', examples: [['flor', 'flores'], ['luz', 'luzes'], ['hífen', 'hifens']] },
  { ending: '-s, stressed on the last syllable', change: 'add -es', examples: [['país', 'países'], ['mês', 'meses']] },
  { ending: '-s or -x, not stressed on the last syllable', change: 'no change — only article shows number', examples: [['o lápis', 'os lápis'], ['o ônibus', 'os ônibus'], ['o tórax', 'os tórax']] },
  { ending: '-m', change: '-m → -ns', examples: [['homem', 'homens'], ['jardim', 'jardins']] },
  { ending: '-l', change: 'drop -l → -is: -ais / -éis / -óis / -uis', examples: [['animal', 'animais'], ['papel', 'papéis'], ['lençol', 'lençóis'], ['azul', 'azuis']] },
  { ending: '-il', change: 'stressed → -is; unstressed → -eis', examples: [['funil', 'funis'], ['fácil', 'fáceis']] },
  {
    ending: 'irregulars',
    change: 'learn per word',
    examples: [['cão', 'cães'], ['alemão', 'alemães'], ['mal', 'males'], ['cônsul', 'cônsules'], ['caráter', 'caracteres']],
  },
]

function PluralsReference() {
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const playForm = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }

  return (
    <>
      <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-[25%] px-4 py-2.5 font-semibold">Singular ends in…</th>
                <th className="w-[24%] px-4 py-2.5 font-semibold">Plural</th>
                <th className="px-4 py-2.5 font-semibold">Examples</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PLURAL_RULES.map((row) => (
                <tr key={row.ending}>
                  <th scope="row" className="px-4 py-2 text-left align-top font-semibold text-slate-800">{row.ending}</th>
                  <td className="px-4 py-2 align-top text-slate-600">{row.change}</td>
                  <td className="px-4 py-2 align-top">
                    {/* Compact: one wrapping line of comma-separated pairs. Only the plural is tap-to-hear — the singular is the word you already know. */}
                    <span translate="no" className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                      {row.examples.map(([singular, plural], index) => (
                        <span key={index} className="inline-flex items-baseline">
                          <span className="text-slate-700">{singular}</span>
                          <span aria-hidden="true" className="mx-1 text-slate-300">→</span>
                          {/* The comma lives inside the button's text so it hugs the word and wraps with it; only the plural is spoken. */}
                          <span className="-ml-1.5">
                            <SpeakableWord
                              text={index < row.examples.length - 1 ? `${plural},` : plural}
                              speakAs={speechForm(plural)}
                              wrap
                              playKey={`pl:${row.ending}:${index}`}
                              playingKey={playingKey}
                              onPlay={playForm}
                            />
                          </span>
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Notes" />
        <div className="space-y-3 px-5 py-4 text-sm leading-relaxed text-slate-600">
          <p>
            The whole noun phrase agrees, article, noun and adjective all go plural. (e.g.{' '}
            <span translate="no" className="italic">o carro novo → os carros novos</span>,{' '}
            <span translate="no" className="italic">a casa azul → as casas azuis</span>)
          </p>
          <p>
            A few nouns are used only in the plural:{' '}
            <span translate="no" className="italic">os óculos</span> (glasses), <span translate="no" className="italic">as férias</span> (holidays),{' '}
            <span translate="no" className="italic">os parabéns</span> (congratulations).
          </p>
        </div>
      </article>
    </>
  )
}

// ---------------------------------------------------------------------------
// Comparative — Grammar-tab reference (cheat sheet only, no drill)
// ---------------------------------------------------------------------------

// The three degrees of comparison, the noun/quantity variant of equality,
// the relative superlative (picking one item out of a group of 3+, which
// adds the definite article and "de" as a third connector), and "the
// most/least" with a preference verb (gostar, preferir, querer, …) instead
// of an adjective — mais/menos can't attach to a verb directly, so the
// frame needs the relative clause "o que" ("the [thing] that") instead.
// In `example`, **double asterisks** mark the pattern the row is about, shown in bold.
type ComparativeRow = { label: string; structure: string; structureNote?: string; example: [string, string] }

// "Ela é **mais** alta **do que** ele." → the starred part in bold, the rest as plain text.
function boldPattern(text: string): ReactNode {
  return text.split('**').map((part, index) => (index % 2 === 1 ? <strong key={index} className="font-semibold text-slate-900">{part}</strong> : part))
}
const COMPARATIVE_DEGREE: ComparativeRow[] = [
  { label: 'More/Less', structure: 'mais/menos + adjective + (do) que', structureNote: '"do" is optional', example: ['Ela é **mais** alta **do que** ele.', 'She is taller than him.'] },
  { label: 'Equality — adjective/adverb', structure: 'tão + adjective/adverb + quanto (or como)', structureNote: 'quanto is more common in Brazil', example: ['Ela é **tão** inteligente **quanto** ele.', 'She is as smart as him.'] },
  { label: 'Highest in a group of 3+', structure: 'o/a mais + adjective + de', structureNote: '"o/a" agrees with the subject', example: ['Marta é **a mais** nova **de** todos.', 'Marta is the youngest of all.'] },
  { label: 'Lowest in a group of 3+', structure: 'o/a menos + adjective + de', structureNote: '"o/a" agrees with the subject', example: ['Este é **o menos** caro **de** todos.', 'This one is the least expensive of all.'] },
  { label: 'Irregular for group of 3+', structure: 'o/a + melhor/pior/maior/menor + de', structureNote: '"o/a" agrees with the subject', example: ['O restaurante B é **o melhor de** todos.', 'Restaurant B is the best of all.'] },
  { label: 'Compare with a verb', structure: 'verb + object A + mais/menos (do) que + object B', example: ['Eu gosto de açaí **mais do que** de manga.', 'I like açaí more than mango.'] },
  { label: 'Most/Least — verb', structure: 'é o que eu mais/menos + verb', example: ['Este é **o que eu mais** gosto.', 'This is the one I like the most.'] },
  { label: 'Equality — noun/quantity', structure: 'tanto/tanta/tantos/tantas + noun + quanto', example: ['Eu tenho **tanto** dinheiro **quanto** você.', 'I have as much money as you.'] },
]

// The four suppletive irregulars — an unrelated word replaces mais/menos +
// adjective entirely, in both the comparative and the superlative. Each gets
// one example per degree (positive, comparative, superlative) so the
// progression is visible, not just the isolated word forms.
type IrregularRow = {
  gloss: string
  positive: string
  comparative: string
  superlative: string
  examples: [[string, string], [string, string], [string, string]]
}
const IRREGULAR_COMPARATIVES: IrregularRow[] = [
  {
    gloss: 'good → better → best',
    positive: 'bom / boa',
    comparative: 'melhor',
    superlative: 'o melhor / a melhor',
    examples: [
      ['Este bolo é **bom**.', 'This cake is good.'],
      ['Este bolo é **melhor** do que aquele.', 'This cake is better than that one.'],
      ['Este bolo é **o melhor** de todos.', 'This cake is the best of all.'],
    ],
  },
  {
    gloss: 'bad → worse → worst',
    positive: 'mau / má, ruim',
    comparative: 'pior',
    superlative: 'o pior / a pior',
    examples: [
      ['Este filme é **ruim**.', 'This movie is bad.'],
      ['Este filme é **pior** do que o outro.', 'This movie is worse than the other one.'],
      ['Este filme é **o pior** de todos.', 'This movie is the worst of all.'],
    ],
  },
  {
    gloss: 'big → bigger → biggest',
    positive: 'grande',
    comparative: 'maior',
    superlative: 'o maior / a maior',
    examples: [
      ['A casa dela é **grande**.', 'Her house is big.'],
      ['A casa dela é **maior** do que a minha.', 'Her house is bigger than mine.'],
      ['A casa dela é **a maior** da rua.', 'Her house is the biggest on the street.'],
    ],
  },
  {
    gloss: 'small → smaller → smallest',
    positive: 'pequeno / pequena',
    comparative: 'menor',
    superlative: 'o menor / a menor',
    examples: [
      ['Este quarto é **pequeno**.', 'This room is small.'],
      ['Este quarto é **menor** do que o outro.', 'This room is smaller than the other one.'],
      ['Este quarto é **o menor** da casa.', 'This room is the smallest in the house.'],
    ],
  },
]

function ComparativeReference() {

  const degreeTable = (rows: ComparativeRow[]) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[44rem] text-left text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-4 py-2.5 font-semibold">Type</th>
            <th className="px-4 py-2.5 font-semibold">Structure</th>
            <th className="px-4 py-2.5 font-semibold">Example</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row" className="whitespace-nowrap px-4 py-2 text-left align-top font-semibold text-slate-800">{row.label}</th>
              <td className="px-4 py-2 align-top italic text-slate-600">
                <span className="flex flex-col gap-y-0.5">
                  <span translate="no">{row.structure}</span>
                  {row.structureNote && <span className="text-xs text-slate-400">{row.structureNote}</span>}
                </span>
              </td>
              <td className="px-4 py-2 align-top">
                <span className="flex flex-col gap-y-0.5">
                  <span translate="no" className="text-slate-600">{boldPattern(row.example[0])}</span>
                  <span className="text-xs text-slate-400">{row.example[1]}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Comparison" />
        {degreeTable(COMPARATIVE_DEGREE)}
      </article>

      <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
        <CardHeader title="Irregular Comparison">
          Irregular adjectives don't follow "mais/menos + adjective" structure. Never "mais bom / mau / grande / pequeno"
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-2.5 font-semibold">English</th>
                <th className="px-4 py-2.5 font-semibold">Portuguese</th>
                <th className="px-4 py-2.5 font-semibold">Examples</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {IRREGULAR_COMPARATIVES.map((row) => (
                <tr key={row.gloss}>
                  <th scope="row" className="whitespace-nowrap px-4 py-2 text-left align-top font-semibold text-slate-800">{row.gloss}</th>
                  <td translate="no" className="whitespace-nowrap px-4 py-2 align-top font-semibold text-slate-700">
                    <span className="flex flex-col gap-y-0.5">
                      <span>{row.positive}</span>
                      <span>{row.comparative}</span>
                      <span>{row.superlative}</span>
                    </span>
                  </td>
                  <td className="w-full px-4 py-2 align-top">
                    <span className="flex flex-col gap-y-1">
                      {row.examples.map(([pt, en], index) => (
                        <span key={index} className="flex items-baseline gap-x-1.5 whitespace-nowrap">
                          <span translate="no" className="text-slate-700">{boldPattern(pt)}</span>
                          <span className="text-xs text-slate-400">— {en}</span>
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </>
  )
}

// ---------------------------------------------------------------------------
// Subjunctive — Grammar-tab reference (cheat sheet only, no drill)
// ---------------------------------------------------------------------------

// The setups that call for the present subjunctive in the clause after "que",
// each with its worked example(s). `note` is a small aside under the row's cues
// (the acho / não acho contrast).
// In the examples, **double asterisks** mark the cue and the subjunctive verb it triggers, shown in bold.
type SubjunctiveUseCase = { label: string; cues: string[]; note?: string; examples: Array<[string, string]> }
const SUBJUNCTIVE_USE_CASES: SubjunctiveUseCase[] = [
  {
    label: 'Wanting',
    cues: ['querer que', 'desejar que', 'esperar que', 'preferir que', 'pedir que'],
    examples: [['**Quero que** você **fale** mais devagar.', 'I want you to speak more slowly.']],
  },
  {
    label: 'Doubt',
    cues: ['duvidar que', 'talvez', 'é possível que', 'não achar que'],
    note: 'acho que takes the indicative; não acho que takes the subjunctive.',
    examples: [
      ['**Talvez** ele **queira** café.', 'Maybe he wants coffee.'],
      ['**Duvido que** **chova** hoje.', "I doubt it'll rain today."],
    ],
  },
  { label: 'Feeling', cues: ['ficar feliz/triste que', 'ter medo que', 'é bom que'], examples: [] },
  {
    label: 'Need',
    cues: ['é preciso que', 'é importante que', 'é necessário que', 'exigir que'],
    examples: [['**É importante que** ela **venha**.', "It's important that she comes."]],
  },
  { label: 'Purpose', cues: ['para que', 'sem que', 'antes que', 'caso'], examples: [] },
]

function SubjunctiveReference() {

  return (
    <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
      <CardHeader title="Use Cases">
          Subjunctive is for things that aren't established facts: wishes, doubts, feelings, and needs. It often shows up in a subordinate
          clause after <span translate="no" className="italic">que</span>.
        </CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="w-[9%] px-4 py-2.5 font-semibold">Type</th>
              <th className="w-[42%] px-4 py-2.5 font-semibold">Cues</th>
              <th className="px-4 py-2.5 font-semibold">Example</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {SUBJUNCTIVE_USE_CASES.map((row) => (
              <tr key={row.label}>
                <th scope="row" className="whitespace-nowrap px-4 py-2 text-left align-top font-semibold text-slate-800">{row.label}</th>
                <td className="px-4 py-2 align-top">
                  {/* Plain text, comma-separated: the cues are for reading, so no tap-to-hear. */}
                  <span translate="no" className="text-slate-700">{row.cues.join(', ')}</span>
                  {row.note && <span className="mt-1 block text-xs text-slate-400">{row.note}</span>}
                </td>
                <td className="px-4 py-2 align-top">
                  {row.examples.length === 0 ? (
                    <span className="text-slate-300">—</span>
                  ) : (
                    <span className="flex flex-col gap-y-1.5">
                      {row.examples.map(([pt, en]) => (
                        // Always two lines: the Portuguese (plain, with the cue and the verb it triggers in
                        // bold — no tap-to-hear), then its English under it.
                        <span key={pt} className="flex flex-col">
                          <span translate="no" className="text-slate-700">{boldPattern(pt)}</span>
                          <span className="text-xs text-slate-400">{en}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  )
}

// A card of two or three small tables side by side, one per word being compared
// (já | ainda, ser | estar | ficar). Each table is the word as a heading over a
// Use column and an Example column, so a row reads across and the words can be
// compared at a glance. They stack on phones.
type SideBySideColumn = {
  key: string
  word: string
  // True when the heading is English (so it can be translated), not a Portuguese word.
  plain?: boolean
  // A few grey words next to the heading ("what something is").
  note?: ReactNode
  rows: Array<{ key: string; use: ReactNode; example: ReactNode }>
}

function SideBySideCard({
  title,
  decision,
  columns,
  gridClass = 'md:grid-cols-2',
  footer,
}: {
  title: string
  decision?: ReactNode
  columns: SideBySideColumn[]
  gridClass?: string
  footer?: ReactNode
}) {
  return (
    <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3 first:mt-0">
      <CardHeader title={title}>{decision}</CardHeader>
      <div className={`grid divide-slate-100 md:divide-x ${gridClass}`}>
        {columns.map((column) => (
          <div key={column.key} className="min-w-0">
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[42%]" />
                <col />
              </colgroup>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th colSpan={2} className="px-4 pt-2.5 pb-1 text-left text-sm font-semibold">
                    <span translate={column.plain ? undefined : 'no'}>{column.word}</span>
                    {column.note && <span className="ml-2 text-xs font-normal text-slate-400">{column.note}</span>}
                  </th>
                </tr>
                <tr className="text-xs font-semibold text-slate-400">
                  <th className="w-[42%] px-4 pb-2 text-left">Use</th>
                  <th className="px-4 pb-2 text-left">Example</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {column.rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row" className="px-4 py-2 text-left align-top font-semibold text-slate-800">{row.use}</th>
                    <td className="px-4 py-2 align-top">{row.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      {footer}
    </article>
  )
}

// ---------------------------------------------------------------------------
// Nuances — Grammar-tab reference (cheat sheet only, no drill)
// ---------------------------------------------------------------------------

function NuancesReference() {
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const playForm = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }
  const speak = (form: string, key: string) => (
    <SpeakableWord text={form} speakAs={speechForm(form)} wrap playKey={key} playingKey={playingKey} onPlay={playForm} />
  )
  // Examples are always two lines: the Portuguese (plain text, only the focus word in bold —
  // no tap-to-hear), then its English in grey right under it.
  const examplesInline = (items: Array<[string, string]>) => (
    <span className="flex flex-col gap-y-1.5">
      {items.map(([pt, en]) => (
        <span key={pt} className="flex flex-col">
          <span translate="no" className="text-slate-700">{boldPattern(pt)}</span>
          <span className="text-xs text-slate-400">{en}</span>
        </span>
      ))}
    </span>
  )
  const card = (title: string, decision: ReactNode, table: ReactNode) => (
    <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3 first:mt-0">
      <CardHeader title={title}>{decision}</CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[44rem] text-left text-sm">{table}</table>
      </div>
    </article>
  )
  const head = (...columns: string[]) => (
    <thead className="bg-slate-50 text-slate-600">
      <tr>
        {columns.map((column) => (
          <th key={column} className="px-4 py-2.5 font-semibold">{column}</th>
        ))}
      </tr>
    </thead>
  )

  return (
    <>
      <SideBySideCard
        title="Muito"
        decision={MUITO_DECISION}
        columns={MUITO_COLUMNS.map((column) => ({
          key: column.id,
          word: column.title,
          plain: true,
          rows: column.rows.map((row) => ({
            key: row.use,
            use: row.use,
            example: examplesInline([row.example]),
          })),
        }))}
        gridClass="md:grid-cols-[4fr_5fr]"
      />

      <SideBySideCard
        title="já vs. ainda"
        decision={TIMING_DECISION}
        columns={TIMING_COLUMNS.map((column) => ({
          key: column,
          word: column,
          rows: TIMING_ROWS.filter((row) => row.column === column).map((row) => ({
            key: row.english,
            use: (
              <>
                {row.english}
                {/* "ainda não" and "não… mais" aren't just the column word, so say which form it is (plain text). */}
                {row.portuguese !== column && (
                  <span translate="no" className="mt-0.5 block font-normal text-slate-500">{row.portuguese}</span>
                )}
              </>
            ),
            example: examplesInline(row.examples),
          })),
        }))}
      />

      <SideBySideCard
        title="velho vs. antigo"
        decision={OLD_DECISION}
        columns={OLD_WORDS.map(({ id, note }) => ({
          key: id,
          word: id,
          note,
          rows: OLD_ROWS.filter((row) => row.word === id).map((row) => ({
            key: row.use,
            use: (
              <>
                {row.use}
                {row.note && <span className="mt-0.5 block text-xs font-normal text-slate-500">{row.note}</span>}
              </>
            ),
            example: examplesInline([row.example]),
          })),
        }))}
        footer={<p className="border-t border-slate-100 px-5 py-3 text-sm text-slate-600">{OLD_QUICK_TEST}</p>}
      />

      {card(
        'aqui vs. aí vs. ali vs. lá',
        DEIXIS_DECISION,
        <>
          {head('Adverb', 'Distance', 'Matching demonstrative', 'Example')}
          <tbody className="divide-y divide-slate-100">
            {DEIXIS_ROWS.map((row) => (
              <tr key={row.adverbs.join('/')}>
                <th scope="row" translate="no" className="whitespace-nowrap px-4 py-2 text-left align-top font-semibold text-slate-800">
                  <span className="inline-flex items-baseline gap-x-1">
                    {row.adverbs.map((adverb, index) => (
                      <span key={adverb} className="inline-flex items-baseline gap-x-1">
                        {index > 0 && <span aria-hidden="true" className="text-slate-300">/</span>}
                        {speak(adverb, `nu-deixis:${adverb}`)}
                      </span>
                    ))}
                  </span>
                </th>
                <td className="px-4 py-2 align-top text-slate-600">{row.distance}</td>
                <td translate="no" className="px-4 py-2 align-top text-slate-600">{row.demonstratives}</td>
                {/* a row's examples sit side by side, so aqui / cá is no taller than the others */}
                <td className="px-4 py-2 align-top">
                  <span className="flex flex-wrap gap-x-6 gap-y-1.5">
                    {row.examples.map(([pt, en]) => (
                      <span key={pt} className="flex flex-col">
                        <span translate="no" className="text-slate-700">{boldPattern(pt)}</span>
                        <span className="text-xs text-slate-400">{en}</span>
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </>,
      )}

      {card(
        'Diminutives',
        DIMINUTIVE_DECISION,
        <>
          {head('Effect on tone', 'Example', 'Ending rule')}
          <tbody className="divide-y divide-slate-100">
            {DIMINUTIVE_ROWS.map((row) => (
              <tr key={row.example}>
                <th scope="row" className="whitespace-nowrap px-4 py-2 text-left align-top font-semibold text-slate-800">{row.effect}</th>
                <td translate="no" className="px-4 py-2 align-top">
                  {/* base word → diminutive; only the diminutive is tap-to-hear */}
                  <span className="inline-flex items-baseline">
                    <span className="text-slate-600">{row.base}</span>
                    <span aria-hidden="true" className="mx-1.5 text-slate-300">→</span>
                    <span className="-ml-1.5">{speak(row.example, `nu-dim:${row.example}`)}</span>
                  </span>
                </td>
                <td translate="no" className="px-4 py-2 align-top text-slate-600">{row.rule}</td>
              </tr>
            ))}
          </tbody>
        </>,
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Verb choice — Grammar-tab reference (cheat sheet only, no drill)
// ---------------------------------------------------------------------------

function VerbChoiceReference() {
  // The tables say what each row is for, so these cards have no description beside the title.
  const card = (title: string, children: ReactNode, footer?: ReactNode) => (
    <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3 first:mt-0">
      <CardHeader title={title} />
      <div className="overflow-x-auto">{children}</div>
      {footer}
    </article>
  )
  const head = (...columns: ReactNode[]) => (
    <thead className="bg-slate-50 text-slate-600">
      <tr>
        {columns.map((column, index) => (
          <th key={index} className="px-4 py-2.5 font-semibold">{column}</th>
        ))}
      </tr>
    </thead>
  )
  return (
    <>
      <SideBySideCard
        title="ser vs. estar vs. ficar"
        columns={BE_VERBS.map(({ verb, gloss }) => ({
          key: verb,
          word: verb,
          note: boldPattern(gloss),
          rows: BE_ROWS.filter((row) => row.verb === verb).map((row) => ({
            key: row.portuguese,
            use: row.use,
            // The Portuguese, with the English phrase the learner starts from right under it.
            example: (
              <span className="flex flex-col gap-y-0">
                <span translate="no" className="text-slate-700">{boldPattern(row.portuguese)}</span>
                <span className="text-xs text-slate-400">{row.english}</span>
              </span>
            ),
          })),
        }))}
        gridClass="md:grid-cols-[5fr_5fr_4fr]"
        footer={<p className="border-t border-slate-100 px-5 py-3 text-sm text-slate-600">{BE_QUICK_TEST}</p>}
      />

      {card(
        'can / be able to',
        <table className="w-full min-w-[44rem] text-left text-sm">
          {head('English trigger', 'Portuguese', 'Why')}
          <tbody className="divide-y divide-slate-100">
            {CAN_ROWS.map((row) => (
              <tr key={row.english}>
                <th scope="row" className="px-4 py-1.5 text-left align-top font-medium text-slate-700">{row.english}</th>
                <td translate="no" className="px-4 py-1.5 align-top text-slate-700">{boldPattern(row.portuguese)}</td>
                <td className="px-4 py-1.5 align-top text-slate-600">{row.why}</td>
              </tr>
            ))}
          </tbody>
        </table>,
      )}

      {card(
        'have done / have been doing',
        <table className="w-full min-w-[44rem] text-left text-sm">
          {head('What you mean', 'Use', 'Example')}
          <tbody className="divide-y divide-slate-100">
            {PERFECT_ROWS.map((row) => (
              <tr key={row.meaning}>
                <th scope="row" className="px-4 py-2 text-left align-top font-semibold text-slate-800">
                  {row.meaning}
                  {row.note && <span className="block text-xs font-normal text-slate-400">{row.note}</span>}
                </th>
                <td translate="no" className="px-4 py-2 align-top text-slate-700">{boldPattern(row.use)}</td>
                {/* a row's examples sit side by side, each with its English underneath */}
                <td className="px-4 py-2 align-top">
                  <span className="flex flex-wrap gap-x-6 gap-y-1.5">
                    {row.examples.map(([pt, en]) => (
                      <span key={pt} className="flex flex-col">
                        <span translate="no" className="text-slate-700">{boldPattern(pt)}</span>
                        <span className="text-xs text-slate-400">{en}</span>
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>,
        <div className="space-y-1 border-t border-slate-100 px-5 py-3 text-sm text-slate-600">
          <p>{PERFECT_QUICK_TEST}</p>
          <p>Participle: {PARTICIPLE_RULE}.</p>
          <p>Irregular: {IRREGULAR_PARTICIPLES.map(([verb, participle]) => `${verb} → ${participle}`).join(', ')}.</p>
        </div>,
      )}
    </>
  )
}

// `chip`: an auto-width, content-sized pill for a wrapping filter row (Tenses,
// Verb types, Pronouns). Selected state is a light purple fill with a modest
// radius — deliberately lighter and less rounded than the solid, fully-pill
// Topic toggle. Default: the roomier card-style box used elsewhere.
function Choice({ checked, label, onChange, chip = false }: { checked: boolean; label: ReactNode; onChange: () => void; chip?: boolean }) {
  const shape = chip
    ? 'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5'
    : 'flex min-w-0 items-center gap-2.5 rounded-xl border px-3.5 py-2 leading-tight'
  return (
    <label
      className={`cursor-pointer text-sm font-medium transition-colors ${shape} ${checked ? 'border-purple-300 bg-purple-50 text-purple-950' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
    >
      <input type="checkbox" checked={checked} onChange={onChange} className="size-4 shrink-0 rounded border-slate-300 text-purple-700 focus:ring-purple-600" />
      {label}
    </label>
  )
}

// ---------------------------------------------------------------------------
// Drill screen
// ---------------------------------------------------------------------------

function Drill({
  items,
  onExit,
  onComplete,
}: {
  items: DrillItem[]
  onExit: () => void
  onComplete: (score: DrillScore) => void
}) {
  const [total] = useState(items.length)
  // The list being worked through right now. After each pass the missed items
  // are re-queued into a fresh `batch` (a "Review round") until none are left.
  const [batch, setBatch] = useState(items)
  const [pass, setPass] = useState(0)
  const [firstPassCorrect, setFirstPassCorrect] = useState(0)
  const [passMissed, setPassMissed] = useState<DrillItem[]>([])
  const [position, setPosition] = useState(0)
  const [answer, setAnswer] = useState('')
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null)
  const [muted, setMuted] = useState(false)
  // Index of the sentence word currently being read aloud (0 = first word …
  // beforeWords.length = the answer input … then the words after), or null.
  const [spokenWord, setSpokenWord] = useState<number | null>(null)
  // After an answer is submitted the sentence is read aloud once; Continue
  // stays disabled until that finishes (or immediately when sound is off).
  const [audioDone, setAudioDone] = useState(false)
  // What to actually speak once answered — item.sentence by default, but for
  // a correct answer with multiple accepted full-sentence phrasings (e.g.
  // "quanto" vs "como"), whichever one the learner actually typed, so the
  // audio never contradicts what's shown on screen.
  const [spokenSentence, setSpokenSentence] = useState('')
  // Inline "why is this verb regular here?" popover for conditional stem-changers,
  // triggered by the infinitive parenthetical itself.
  const [hintOpen, setHintOpen] = useState(false)
  // "may not make logical sense" disclaimer tooltip next to the translation.
  const [disclaimerOpen, setDisclaimerOpen] = useState(false)
  // The Portuguese accent keypad is opt-in — most people type on a keyboard
  // that already has the accents. Hidden until asked for, and again on each
  // new prompt.
  const [accentsOpen, setAccentsOpen] = useState(false)
  // Word-lookup hints (e.g. Comparisons: "puzzle = quebra-cabeça") are opt-in
  // too — shown only on request, so an unfamiliar noun/adjective doesn't
  // block practicing the actual grammar point. Hidden again on each new prompt.
  const [vocabHintsOpen, setVocabHintsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const hintTriggerRef = useRef<HTMLSpanElement>(null)
  const disclaimerRef = useRef<HTMLSpanElement>(null)
  const vocabHintsRef = useRef<HTMLSpanElement>(null)
  const continueRef = useRef<HTMLButtonElement>(null)
  const item = batch[position]
  const inputSlot = item.beforeWords.length
  // Punctuation that follows the blank goes after the cue: "… que ___ (chover)." / "___ (repetir), por favor."
  const punctuationAfterCue = Boolean(item.cueText)
  const moreToReview = passMissed.length > 0
  const lastInBatch = position === batch.length - 1

  // A fresh drill starts at the top, whichever button launched it.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  useEffect(() => {
    if (!hintOpen && !disclaimerOpen && !vocabHintsOpen) return
    // A pointer down outside a trigger closes its popover but is NOT consumed —
    // the click still focuses the input / presses a button normally.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (!hintTriggerRef.current?.contains(target)) setHintOpen(false)
      if (!disclaimerRef.current?.contains(target)) setDisclaimerOpen(false)
      if (!vocabHintsRef.current?.contains(target)) setVocabHintsOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHintOpen(false)
        setDisclaimerOpen(false)
        setVocabHintsOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [hintOpen, disclaimerOpen, vocabHintsOpen])

  const wordClass = (tokenIndex: number) => (spokenWord === tokenIndex ? 'rounded bg-amber-200 text-slate-950' : undefined)

  function playSentence(afterEnd?: () => void) {
    setSpokenWord(null)
    return speak(
      spokenSentence,
      muted,
      () => {
        setSpokenWord(null)
        afterEnd?.()
      },
      (charIndex) => {
        // The audio is only the answer ("os jornais"), so its words don't line up with the prompt's.
        if (item.speaksAnswerOnly) return setSpokenWord(inputSlot)
        const starts = [...spokenSentence.matchAll(/\S+/g)].map((match) => match.index ?? 0)
        let index = 0
        for (let i = 0; i < starts.length; i += 1) if (charIndex >= starts[i]) index = i
        setSpokenWord(index)
      },
    )
  }

  useEffect(() => {
    if (feedback) return
    inputRef.current?.focus()
    // On mobile, the on-screen keyboard's own toolbar (prev/next/done)
    // floats right above the keyboard and can end up sitting exactly where
    // the Check button naturally falls, swallowing taps meant for it.
    // Anchoring to the *card's* top edge (rather than nudging by however
    // much the Check button overlaps the keyboard) gives the same result on
    // every question regardless of how many lines the prompt wraps to —
    // otherwise a short prompt and a long one land the button at different
    // heights and need different amounts of scroll. Desktop has no keyboard
    // to avoid, so skip it there.
    if (typeof window === 'undefined' || window.innerWidth >= 640) return
    const CARD_TOP_MARGIN = 16 // breathing room above the card, in px
    const timer = setTimeout(() => {
      const card = cardRef.current
      if (!card) return
      const targetTop = card.getBoundingClientRect().top + window.scrollY - CARD_TOP_MARGIN
      window.scrollTo({ top: targetTop, behavior: 'smooth' })
    }, 350)
    return () => clearTimeout(timer)
  }, [feedback, position])

  // Collapse the accent keypad whenever the prompt changes.
  useEffect(() => setAccentsOpen(false), [position, pass])
  // Collapse vocab hints whenever the prompt changes, so they don't carry
  // over and accidentally spoil the next item.
  useEffect(() => setVocabHintsOpen(false), [position, pass])

  // After every answer (right or wrong) read the whole sentence aloud once.
  // Continue is gated on `audioDone`, which flips when speech ends — or right
  // away when sound is muted / unavailable. A timer backs up the end event,
  // since it's known to not always fire on iOS Safari (worse still after the
  // audio-session priming used to route speech to Bluetooth — see
  // primeAudioSessionForBluetooth). Sized to the sentence rather than a flat
  // delay, so a missed event doesn't leave Continue looking stuck for
  // several seconds after the audio has clearly already finished.
  useEffect(() => {
    if (!feedback) return
    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      setAudioDone(true)
    }
    const speaking = playSentence(settle)
    const wordCount = spokenSentence.trim().split(/\s+/).filter(Boolean).length
    const fallbackDelay = Math.min(8000, Math.max(1500, wordCount * 400))
    const timer = setTimeout(settle, speaking ? fallbackDelay : 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback])

  // Once Continue is enabled, move focus to it so keyboard users can advance.
  useEffect(() => {
    if (feedback && audioDone) continueRef.current?.focus()
  }, [feedback, audioDone])

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!answer.trim() || feedback) return
    primeAudioSessionForBluetooth()
    // Case-insensitive but accent-exact ("a" for "à" is wrong); lenient on
    // trailing punctuation and inner spacing. Object-pronoun items accept a
    // list of forms; every other topic has the single `answer`.
    const accepted = item.acceptedAnswers ?? [item.answer]
    const target = normalizeForCompare(item.clusterOf ? item.clusterOf(answer) : answer)
    const matched = accepted.find((candidate) => normalizeForCompare(candidate) === target)
    setFeedback(matched !== undefined ? 'correct' : 'incorrect')
    // On a correct answer that used a different accepted phrasing than the
    // canonical one (e.g. "como" instead of "quanto"), speak that phrasing
    // back — never contradict what's on screen. Only safe to substitute when
    // the answer is the whole sentence (no fixed before/after words around it).
    setSpokenSentence(
      matched !== undefined && item.sentenceFor
        ? item.sentenceFor(matched)
        : matched !== undefined && item.beforeWords.length === 0 && item.afterWords.length === 0
          ? `${matched}.`
          : item.sentence,
    )
    if (matched !== undefined) {
      if (pass === 0) setFirstPassCorrect((count) => count + 1)
    } else {
      setPassMissed((missed) => [...missed, item])
    }
  }

  function next() {
    setSpokenWord(null)
    setHintOpen(false)
    setDisclaimerOpen(false)
    setAudioDone(false)
    if (!lastInBatch) {
      setPosition(position + 1)
      setAnswer('')
      setFeedback(null)
      return
    }
    // End of the pass. Re-drill anything missed, else the session is done.
    if (moreToReview) {
      setBatch(passMissed)
      setPassMissed([])
      setPass((count) => count + 1)
      setPosition(0)
      setAnswer('')
      setFeedback(null)
      return
    }
    onComplete({ correct: firstPassCorrect, total })
  }

  function insertAccent(character: string) {
    const input = inputRef.current
    const start = input?.selectionStart ?? answer.length
    const end = input?.selectionEnd ?? answer.length
    setAnswer(`${answer.slice(0, start)}${character}${answer.slice(end)}`)
    requestAnimationFrame(() => {
      input?.focus()
      input?.setSelectionRange(start + 1, start + 1)
    })
  }

  const wrongAnswerNote = feedback === 'incorrect' ? (item.answerNote?.(answer) ?? null) : null
  const progress = ((position + (feedback ? 1 : 0)) / batch.length) * 100

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes drill-correction-strike {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes drill-correction-answer {
          0%, 40% { opacity: 0; translate: -0.5rem }
          100% { opacity: 1; translate: 0 }
        }
      `}</style>
      <div className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-8">
        <button type="button" onClick={onExit} aria-label="Exit drill" className="grid size-10 place-items-center rounded-full text-2xl text-slate-500 hover:bg-slate-100 hover:text-slate-950">×</button>
        <div className="h-2 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-label="Drill progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <button type="button" onClick={() => { window.speechSynthesis?.cancel(); setMuted(!muted) }} aria-label={muted ? 'Turn sound on' : 'Mute sound'} className="grid size-10 cursor-pointer place-items-center rounded-full text-blue-600 hover:bg-slate-100 hover:text-blue-700">{muted ? <SpeakerOffIcon className="size-6" /> : <SpeakerWaveOutlineIcon className="size-6" />}</button>
      </div>

      <main className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-14">
        <div ref={cardRef} className="flex flex-col items-center rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-slate-200 sm:p-8">
          {pass > 0 && <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-amber-600">Review round</p>}
          <span translate="no" className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blue-800">{item.badge}</span>

          {item.promptLines && item.promptLines.length > 0 && (
            <div className="mt-5 w-full space-y-1 text-lg font-medium leading-snug text-slate-800">
              {item.promptLines.map((line, index) => {
                const isLast = index === item.promptLines!.length - 1
                const showHintTrigger = isLast && item.vocabHints && item.vocabHints.length > 0 && !feedback
                return (
                  <p key={`prompt-${index}`} className={isLast ? 'text-slate-950' : 'text-sm text-slate-500'}>
                    {line}
                    {showHintTrigger && (
                      <>
                        {' '}
                        <span ref={vocabHintsRef} className="relative inline-block">
                          <button
                            type="button"
                            onClick={() => setVocabHintsOpen((open) => !open)}
                            onMouseEnter={() => setVocabHintsOpen(true)}
                            onMouseLeave={() => setVocabHintsOpen(false)}
                            aria-expanded={vocabHintsOpen}
                            aria-label="Show word hints"
                            className="inline-flex cursor-pointer align-text-bottom text-slate-400 transition-colors hover:text-amber-500 focus:outline-none focus-visible:text-amber-500"
                          >
                            <LightBulbIcon className="size-5" />
                          </button>
                          {vocabHintsOpen && (
                            <span className="absolute left-1/2 top-full z-10 -translate-x-1/2 pt-1.5">
                              <span role="tooltip" className="flex w-max max-w-64 flex-wrap items-baseline gap-x-1.5 gap-y-1 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg sm:max-w-72">
                                {/* flex-wrap (not inline text wrapping) so each "en = pt" pair
                                    always wraps as a whole unit onto its own line, never splitting
                                    a multi-word hint like "a vacation" mid-phrase. */}
                                {item.vocabHints!.map((hint, hintIndex) => (
                                  <span key={hint.en} className="whitespace-nowrap">
                                    {hintIndex > 0 && <span className="mr-1.5 text-slate-500">·</span>}
                                    {hint.en} = <span translate="no" className="font-medium">{hint.pt}</span>
                                  </span>
                                ))}
                              </span>
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </p>
                )
              })}
            </div>
          )}

          <form onSubmit={submit} className={item.promptLines ? 'mt-4 w-full' : 'mt-6 w-full'}>
            <div data-answer={item.answer} className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3 text-2xl font-semibold leading-relaxed text-slate-950">
              {item.beforeWords.map((word, index) => (
                <span key={`before-${index}`} translate="no" className={wordClass(index)}>{word}</span>
              ))}
              {feedback === 'incorrect' ? (
                // Whole-word correction: the user's answer struck through, then the
                // full correct word intact. No fragmenting, no underline — the
                // strikethrough alone is the non-colour signal. Both spans inherit
                // the sentence's size and weight.
                <span translate="no" className={`inline-flex items-baseline gap-x-1.5 border-b-2 border-red-600 pb-1 ${spokenWord === inputSlot ? 'rounded bg-amber-200' : ''}`}>
                  <s className="text-red-600" style={{ animation: 'drill-correction-strike 150ms ease-out' }}>
                    {answer.trim()}
                  </s>
                  <span className="text-emerald-700" style={{ animation: 'drill-correction-answer 250ms ease-out' }}>
                    {item.answer}
                  </span>
                </span>
              ) : (
                <input ref={inputRef} aria-label={item.answerAriaLabel} autoComplete="off" autoCapitalize="none" spellCheck={false} disabled={feedback !== null} value={answer} onChange={(event) => setAnswer(event.target.value)} className={`min-w-40 max-w-full border-x-0 border-t-0 px-2 py-1 text-center [field-sizing:content] focus:outline-none focus:ring-0 sm:min-w-52 ${spokenWord === inputSlot ? 'bg-amber-200' : 'bg-transparent'} ${feedback === 'correct' ? 'border-b-2 border-emerald-700 text-emerald-700' : 'border-slate-400 focus:border-purple-700'}`} />
              )}
              {item.suffix && !punctuationAfterCue && <span className="-ml-1">{item.suffix}</span>}
              {item.stemChangeVerb ? (
                <span
                  ref={hintTriggerRef}
                  className="relative inline-flex"
                  onMouseEnter={() => setHintOpen(true)}
                  onMouseLeave={() => setHintOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => setHintOpen((open) => !open)}
                    aria-expanded={hintOpen}
                    aria-controls={hintOpen ? 'stem-change-hint' : undefined}
                    className="cursor-pointer border-b border-dotted border-blue-600 font-normal text-slate-500 transition-colors hover:border-blue-700 focus:outline-none focus-visible:border-blue-700"
                  >
                    (<span translate="no">{item.cueText}</span>)
                  </button>
                  {hintOpen && (
                    <span className="absolute left-1/2 top-full z-10 -translate-x-1/2 pt-1.5">
                      <span
                        id="stem-change-hint"
                        role="tooltip"
                        className="block w-max max-w-64 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg sm:max-w-72"
                      >
                        {stemChangeExplanation(item.stemChangeVerb)}
                      </span>
                    </span>
                  )}
                </span>
              ) : item.cueText ? (
                <span className={`font-normal text-slate-500 transition-opacity duration-200 ${feedback ? 'opacity-50' : 'opacity-100'}`}>
                  (<span translate="no">{item.cueText}</span>)
                </span>
              ) : null}
              {item.suffix && punctuationAfterCue && <span className="-ml-2">{item.suffix}</span>}
              {item.afterWords.map((word, index) => (
                <span key={`after-${index}`} translate="no" className={wordClass(inputSlot + 1 + index)}>{word}</span>
              ))}
              {feedback && (
                <button
                  type="button"
                  disabled={muted}
                  onClick={() => playSentence()}
                  aria-label="Replay sentence"
                  className="cursor-pointer text-blue-600 transition-colors hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <SpeakerWaveIcon className="size-6" />
                </button>
              )}
              {!feedback && (
                <button
                  type="button"
                  onClick={() => setAccentsOpen((open) => !open)}
                  aria-expanded={accentsOpen}
                  aria-controls="drill-accent-keys"
                  aria-label={accentsOpen ? 'Hide accent keys' : 'Show accent keys'}
                  className="inline-flex items-center gap-1 self-center rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100"
                >
                  <span aria-hidden="true">áçã</span>
                  <span aria-hidden="true" className="text-[10px] text-slate-400">{accentsOpen ? '▲' : '▼'}</span>
                </button>
              )}
            </div>

            {!feedback && (
              <>
                {accentsOpen && (
                  <div id="drill-accent-keys" className="mt-4 flex flex-wrap justify-center gap-2" aria-label="Portuguese accent keys">
                    {'áàâãçéêíóôõú'.split('').map((character) => <button key={character} type="button" onClick={() => insertAccent(character)} className="min-w-9 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100">{character}</button>)}
                  </div>
                )}
                <button type="submit" disabled={!answer.trim()} className="mt-6 w-full cursor-pointer rounded-xl bg-purple-700 px-8 py-3 font-semibold text-white hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-slate-300">Check</button>
              </>
            )}
          </form>

          {feedback && (
            <div className="mt-5 w-full" aria-live="polite">
              {item.contrast && (
                <div className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
                  {(['written', 'spoken'] as const).map((side) => {
                    const active = item.contrast!.active === side
                    return (
                      <span key={side} className={active ? 'font-semibold text-slate-900' : 'text-slate-400'}>
                        {active && <span aria-hidden="true">✓ </span>}
                        {side === 'written' ? 'Formal' : 'Casual'}: <span translate="no">{item.contrast![side]}</span>
                      </span>
                    )
                  })}
                </div>
              )}
              {/* Object-pronoun items accept several natural replies. When the
                  learner nailed a valid one that isn't the canonical form, say
                  so — and point at the most natural phrasing for the situation. */}
              {item.contrast && feedback === 'correct' && normalizeForCompare(item.clusterOf ? item.clusterOf(answer) : answer) !== normalizeForCompare(item.answer) && (
                <p className="mb-3 text-sm text-slate-500">
                  <span aria-hidden="true">✓ </span>That works too. Most natural here:{' '}
                  <span translate="no" className="font-semibold text-slate-700">{item.contrast[item.contrast.active]}</span>
                </p>
              )}
              {wrongAnswerNote && <p className="mb-3 text-sm text-slate-600">{wrongAnswerNote}</p>}
              {feedback === 'incorrect' && (item.tip || item.tipRows) && (
                // A tip with rows is one left-aligned block (lead line, then a rule per row), centred as a whole.
                <div className="mb-3 text-sm text-slate-600">
                  <div className={item.tipRows ? 'inline-block text-left' : undefined}>
                    {item.tip && <p>{item.tip}</p>}
                    {item.tipRows && (
                      <ul translate="no" className={`space-y-0.5 ${item.tip ? 'mt-1' : ''}`}>
                        {item.tipRows.map((row) => (
                          <li key={row}>{row}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {item.translation && (
                <p className="text-xl font-medium leading-snug text-slate-700">
                  {item.translation}
                  {item.disclaimer && (
                    <>
                      {' '}
                      <span ref={disclaimerRef} className="relative inline-block align-baseline">
                        <button
                          type="button"
                          onClick={() => setDisclaimerOpen((open) => !open)}
                          onMouseEnter={() => setDisclaimerOpen(true)}
                          onMouseLeave={() => setDisclaimerOpen(false)}
                          aria-expanded={disclaimerOpen}
                          aria-label="About these example sentences"
                          className="cursor-pointer border-0 bg-transparent p-0 align-baseline text-[0.9em] leading-none text-slate-400 transition-colors hover:text-slate-600 focus:outline-none focus-visible:text-slate-600"
                        >
                          ⓘ
                        </button>
                        {disclaimerOpen && (
                          <span className="absolute left-1/2 top-full z-10 -translate-x-1/2 pt-1.5">
                            <span role="tooltip" className="block w-max max-w-64 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg sm:max-w-72">
                              {item.disclaimer}
                            </span>
                          </span>
                        )}
                      </span>
                    </>
                  )}
                </p>
              )}

              <button
                ref={continueRef}
                type="button"
                disabled={!audioDone}
                onClick={next}
                className="mt-4 w-full cursor-pointer rounded-xl bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {lastInBatch && !moreToReview ? 'Finish' : 'Continue'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Conjugation Tables screen — three navigable sections, no per-verb scrolling
// ---------------------------------------------------------------------------

const CONJ_SECTIONS = [
  { id: 'regular', label: 'Regular' },
  { id: 'stem', label: 'Stem-changing' },
  { id: 'irregular', label: 'Irregular' },
] as const
type ConjSection = (typeof CONJ_SECTIONS)[number]['id']

const ENDING_EXAMPLES: Record<EndingType, { verb: string; stem: string }> = {
  ar: { verb: 'falar', stem: 'fal' },
  er: { verb: 'comer', stem: 'com' },
  ir: { verb: 'partir', stem: 'part' },
}

const REGULAR_CHIPS: Array<{ id: EndingType; label: string }> = [
  { id: 'ar', label: '-ar' },
  { id: 'er', label: '-er' },
  { id: 'ir', label: '-ir' },
]

// Brazilian Portuguese stem-changers (no e→ie / o→ue): a vowel alternation —
// o→u (dormir → durmo) or e→i (sentir → sinto) — or a consonant shift in just
// the eu form and the present subjunctive — d→ç (pedir → peço), c→ç
// (conhecer → conheço), g→j (dirigir → dirijo).
type StemPattern = 'o→u' | 'e→i' | 'd→ç' | 'c→ç' | 'g→j'
const STEM_CHIPS: Array<{ id: StemPattern; label: string }> = [
  { id: 'o→u', label: 'o → u' },
  { id: 'e→i', label: 'e → i' },
  { id: 'd→ç', label: 'd → ç' },
  { id: 'c→ç', label: 'c → ç' },
  { id: 'g→j', label: 'g → j' },
]
const STEM_EXAMPLE: Record<StemPattern, { infinitive: string; pattern: string }> = {
  'o→u': { infinitive: 'dormir', pattern: 'o→u stem change' },
  'e→i': { infinitive: 'sentir', pattern: 'e→i stem change' },
  'd→ç': { infinitive: 'pedir', pattern: 'd→ç stem change' },
  'c→ç': { infinitive: 'conhecer', pattern: 'c→ç stem change' },
  'g→j': { infinitive: 'dirigir', pattern: 'g→j stem change' },
}

function TableHead() {
  return (
    <thead className="bg-slate-50 text-slate-600">
      <tr>
        <th className="px-4 py-3 align-bottom font-semibold">Pronoun</th>
        {TABLE_COLUMNS.map((column) => (
          <th key={column.id} className="px-4 py-3 align-bottom font-semibold">{column.label}</th>
        ))}
      </tr>
    </thead>
  )
}

// The topic switcher at the top of both the Drills and Reference tabs (same
// chips, same position) so "pick a topic" reads as a single pattern. Most
// topics have both a drill and a cheat sheet; Plurals, Subjunctive, Nuances and Verb choice are cheat-sheet only, so
// the Grammar tab passes GRAMMAR_TOPICS and the Drills tab passes TOPICS.
const TOPICS: Array<{ id: Topic; label: string }> = [
  { id: 'conjugation', label: 'Conjugations' },
  { id: 'contraction', label: 'Contractions' },
  { id: 'objectPronoun', label: 'Object pronouns' },
  { id: 'comparative', label: 'Comparisons' },
  { id: 'plurals', label: 'Plurals' },
]
const GRAMMAR_TOPICS: Array<{ id: Topic; label: string }> = [...TOPICS, { id: 'subjunctive', label: 'Subjunctive' }, { id: 'nuances', label: 'Nuances' }, { id: 'verbChoice', label: 'Verb choice' }]

function TopicSwitcher({ topic, topics, onSelect }: { topic: Topic; topics: Array<{ id: Topic; label: string }>; onSelect: (topic: Topic) => void }) {
  return (
    <div className="flex flex-wrap gap-2" aria-label="Grammar topic">
      {topics.map((option) => {
        const active = topic === option.id
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onSelect(option.id)}
            aria-pressed={active}
            // Selected state is always solid brand green, never coral — coral is
            // reserved exclusively for error/destructive states (see the
            // incorrect-answer feedback in Drill), so it must never double as a
            // selection or active-state color anywhere in the app. Fixed hex
            // (not the purple-* token) because this fill stays the same brand
            // green in both light and dark mode, unlike the rest of the palette.
            className={`cursor-pointer rounded-2xl px-4 py-2.5 text-left transition-colors ${active ? 'bg-[#155d47] ring-2 ring-[#155d47]' : 'bg-white ring-1 ring-slate-200 hover:ring-slate-300'}`}
          >
            <span className={`block text-sm font-semibold ${active ? 'text-[#f6f3ea]' : 'text-slate-700'}`}>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function TabShell({ wide = false, kicker, heading, subtitle, aside, topic, topics = TOPICS, onTopic, children }: { wide?: boolean; kicker?: string; heading?: string; subtitle?: string; aside?: ReactNode; topic: Topic; topics?: Array<{ id: Topic; label: string }>; onTopic: (topic: Topic) => void; children: ReactNode }) {
  return (
    <main className={`mx-auto ${wide ? 'max-w-6xl' : 'max-w-4xl'} px-4 py-6 sm:py-10`}>
      {kicker && <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-700">{kicker}</p>}
      {heading && <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">{heading}</h1>}
      {(subtitle || aside) && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p className="text-sm text-slate-500">{subtitle}</p>
          {aside}
        </div>
      )}
      <div className={heading || subtitle || aside ? 'mt-5' : undefined}>
        <FilterRow label="Topic">
          <TopicSwitcher topic={topic} topics={topics} onSelect={onTopic} />
        </FilterRow>
      </div>
      <div className="mt-6">{children}</div>
    </main>
  )
}

type PronounceProps = { playingKey: string | null; onPlay: (key: string, text: string) => void }
type TableProps = PronounceProps & { showTu: boolean }

// Full per-verb table (stem-changing + irregular sections), coloured by the
// render-time diff against the expected regular form. Every word cell is a
// button that pronounces that form aloud.
function ConjugationTable({ infinitive, showTu, playingKey, onPlay }: { infinitive: string } & TableProps) {
  // verbs.json for the ~54 verbs it covers; a rule-based / hand-written table
  // for every other dictionary verb (trazer, subir, pentear …).
  const conjugations = conjugationGridFor(infinitive)
  if (!conjugations) return null
  const nonFinite = gerundAndParticiples(infinitive)
  return (
    <>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[60rem] text-left text-sm">
        <TableHead />
        <tbody translate="no" className="divide-y divide-slate-100">
          {PRONOUNS.filter((pronoun) => showTu || pronoun.id !== 'tu').map((pronoun) => {
            const pronounIndex = (PRONOUN_KEYS as readonly string[]).indexOf(pronoun.id)
            return (
            <tr key={pronoun.id}>
              <th className="px-4 py-3 font-semibold text-slate-800">{pronoun.label}</th>
              {TABLE_COLUMNS.map((column) => {
                const value = conjugations[column.id]?.[pronoun.id] ?? '—'
                const key = `${infinitive}:${column.id}:${pronoun.id}`
                const playing = playingKey === key
                return (
                  <td key={column.id} className="p-0 align-top">
                    {value === '—' ? (
                      <span className="block px-4 py-3 text-slate-300">—</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPlay(key, value)}
                        aria-label={`Play pronunciation of ${value}`}
                        className={`group flex w-full cursor-pointer items-center gap-1.5 px-4 py-3 text-left whitespace-nowrap text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-blue-50 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-300 active:bg-blue-50 ${playing ? 'bg-blue-50' : ''}`}
                      >
                        <span>
                          <FormCell infinitive={infinitive} column={column.id} pronounIndex={pronounIndex} value={value} />
                        </span>
                        <SpeakerWaveOutlineIcon
                          className={`size-3.5 shrink-0 text-blue-600 transition-all duration-150 ${playing ? 'scale-100 opacity-100' : 'scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100'}`}
                        />
                      </button>
                    )}
                  </td>
                )
              })}
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
    {nonFinite && (
      // The two forms the table doesn't have, each tap-to-hear like the cells above: estar + gerund, ter + participle.
      <p translate="no" className="flex flex-wrap items-center gap-x-8 gap-y-1 border-t border-slate-100 px-4 py-2.5 text-sm">
        {[
          { label: 'Gerund', id: 'gerund', forms: [nonFinite.gerund] },
          { label: 'Participle', id: 'participle', forms: nonFinite.participles },
        ].map(({ label, id, forms }) => (
          <span key={id} className="flex items-center gap-x-2">
            <span className="text-slate-500">{label}</span>
            {forms.map((form, index) => {
              const key = `${infinitive}:${id}:${index}`
              return (
                <button
                  key={form}
                  type="button"
                  onClick={() => onPlay(key, form)}
                  aria-label={`Play pronunciation of ${form}`}
                  className={`group flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 font-semibold text-slate-800 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-300 active:bg-blue-50 ${playingKey === key ? 'bg-blue-50' : ''}`}
                >
                  {form}
                  <SpeakerWaveOutlineIcon
                    className={`size-3.5 shrink-0 text-blue-600 transition-all duration-150 ${playingKey === key ? 'scale-100 opacity-100' : 'scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100'}`}
                  />
                </button>
              )
            })}
          </span>
        ))}
      </p>
    )}
    </>
  )
}

// One verb class: the example verb's full conjugation, stem in plain text and
// the pronoun ending in blue (nothing here is irregular by definition).
function EndingsTable({ type, ...tableProps }: { type: EndingType } & TableProps) {
  const example = ENDING_EXAMPLES[type]
  return (
    <article className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
      <CardHeader title={`-${type} verbs`} />
      <ConjugationTable infinitive={example.verb} {...tableProps} />
    </article>
  )
}

function Tables({ section, onSectionChange, showTu, onToggleTu }: { section: ConjSection; onSectionChange: (section: ConjSection) => void; showTu: boolean; onToggleTu: () => void }) {
  const [rawPattern, setRawPattern] = useState<string>('all')
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const irregulars = verbs.filter((verb) => verb.verbType === 'irregular')

  // The "pick a sub-pattern" row for the chosen verb type, with a pinned "All".
  const patternOptions: ReadonlyArray<{ id: string; label: string }> =
    section === 'regular'
      ? REGULAR_CHIPS.map((chip) => ({ id: chip.id, label: chip.label }))
      : section === 'stem'
        ? STEM_CHIPS.map((chip) => ({ id: chip.id, label: chip.label }))
        : irregulars.map((verb) => ({ id: verb.infinitive, label: verb.infinitive }))
  // A value left over from another verb type falls back to "all".
  const pattern = patternOptions.some((option) => option.id === rawPattern) ? rawPattern : 'all'

  // Switching verb type starts back at "all".
  useEffect(() => {
    setRawPattern('all')
  }, [section])

  const playForm = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }
  const tableProps: TableProps = { playingKey, onPlay: playForm, showTu }

  const regularTypes: EndingType[] = pattern === 'all' ? ['ar', 'er', 'ir'] : [pattern as EndingType]
  const stemPatterns: StemPattern[] = pattern === 'all' ? ['o→u', 'e→i', 'd→ç', 'c→ç', 'g→j'] : [pattern as StemPattern]
  const irregularCards = pattern === 'all' ? irregulars : irregulars.filter((verb) => verb.infinitive === pattern)

  return (
    <>
      <div className="space-y-4">
        <FilterRow label="Verb Type" aside={<TableLegend swatch={section === 'regular' ? 'regular' : 'both'} />}>
          <SegmentedControl ariaLabel="Verb type" options={CONJ_SECTIONS} value={section} onChange={onSectionChange} />
        </FilterRow>
        <FilterRow
          label="Pattern"
          aside={
            <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-xs ring-1 ring-slate-200 hover:ring-slate-300">
              <input type="checkbox" checked={showTu} onChange={onToggleTu} className="size-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className="font-medium normal-case text-slate-700">Show tu</span>
            </label>
          }
        >
          <PinnedAllPills ariaLabel="Pattern" options={patternOptions} value={pattern} onChange={setRawPattern} />
        </FilterRow>
      </div>

      <div className="mt-6 space-y-6">
        {section === 'regular' && regularTypes.map((type) => <EndingsTable key={type} type={type} {...tableProps} />)}

        {section === 'stem' &&
          stemPatterns.map((stemPattern) => {
            const card = STEM_EXAMPLE[stemPattern]
            const meaning = verbs.find((verb) => verb.infinitive === card.infinitive)?.englishMeaning
            return (
              <article key={stemPattern} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
                <CardHeader title={card.infinitive}>
                  {meaning}
                  {/* The letter change is orange, like the irregular forms it explains in the table. */}
                  <span className="ml-3 font-semibold text-amber-600">{card.pattern}</span>
                </CardHeader>
                <ConjugationTable infinitive={card.infinitive} {...tableProps} />
              </article>
            )
          })}

        {section === 'irregular' &&
          irregularCards.map((verb) => (
            <article key={verb.infinitive} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
              <CardHeader title={verb.infinitive}>{verb.englishMeaning}</CardHeader>
              <ConjugationTable infinitive={verb.infinitive} {...tableProps} />
            </article>
          ))}
      </div>

      <p className="mt-4 text-sm text-slate-500">
        A gente means 'we' and is more common than nós in everyday speech. It uses the 3rd-person singular form:{' '}
        <span translate="no">a gente fala</span>.
      </p>
    </>
  )
}

// ---------------------------------------------------------------------------
// Vocabulary screen — thematic word lists, every Portuguese form tap-to-hear
// ---------------------------------------------------------------------------

// One tappable Portuguese form: the word plus a speaker icon that fades in on
// hover / focus (and stays lit while its clip plays), matching the reference
// tables. Audio is the same on-device pt-BR speech synthesis used everywhere.
function SpeakableWord({
  text,
  article,
  speakAs,
  wrap = false,
  muted = false,
  playKey,
  playingKey,
  onPlay,
}: {
  text: string
  article?: string
  speakAs?: string // pronounce this instead of `text` (e.g. drop a "(a)" ending)
  wrap?: boolean // allow the label to wrap instead of forcing one line
  muted?: boolean // slate instead of blue — used for the spoken-only variants
  playKey: string
  playingKey: string | null
  onPlay: (key: string, text: string) => void
}) {
  const playing = playingKey === playKey
  // Nouns carry their definite article: shown muted, spoken with the word.
  const base = speakAs ?? text
  const spoken = article ? `${article} ${base}` : base
  const tone = muted
    ? `text-slate-500 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:ring-slate-300 ${playing ? 'bg-slate-100' : ''}`
    : `text-blue-700 hover:bg-blue-50 focus-visible:bg-blue-50 focus-visible:ring-blue-300 ${playing ? 'bg-blue-50' : ''}`
  return (
    <button
      type="button"
      translate="no"
      onClick={() => onPlay(playKey, spoken)}
      aria-label={`Play pronunciation of ${spoken}`}
      className={`group inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-left font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-inset ${tone}`}
    >
      <span className={wrap ? '' : 'whitespace-nowrap'}>
        {article && <span className="font-normal text-slate-400">{article} </span>}
        {text}
      </span>
      <SpeakerWaveOutlineIcon
        className={`size-3.5 shrink-0 transition-all duration-150 ${muted ? 'text-slate-400' : 'text-blue-600'} ${playing ? 'scale-100 opacity-100' : 'scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100'}`}
      />
    </button>
  )
}

// Portuguese forms are stored as one " / "-separated string; split for
// per-form audio. What's spoken drops parenthetical/ellipsis clutter
// ("Quanto(a)" → "Quanto", "Tem...?" → "Tem?") so TTS reads cleanly.
function speechForm(form: string): string {
  return form
    .replace(/\([^)]*\)/g, '')
    .replace(/\.{2,}/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// Level is the primary filter — a segmented control ("All" + each level from
// the data). VOCAB_CATEGORIES is already in learning order (greetings → …),
// which the secondary category row surfaces left-to-right as a soft path.
const VOCAB_LEVEL_SEGMENTS = [{ id: 'all', label: 'All' }, ...VOCAB_LEVELS.map((code) => ({ id: code, label: code }))]

// One filter row: an uppercase micro-label on the left, its control (pills /
// segmented bar) inline to the right, and an optional right-aligned aside
// (a legend, a toggle). Labels share a fixed width so the controls line up.
function FilterRow({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
      <p className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="min-w-0 flex-1">{children}</div>
      {/* w-full on narrow screens: forces the aside (e.g. a legend or a
          "Show tu" toggle) onto its own line instead of squeezing beside
          the filter control and overlapping it. */}
      {aside && <div className="w-full shrink-0 pl-20 sm:w-auto sm:pl-0">{aside}</div>}
    </div>
  )
}

// iOS-style connected tab bar: one slate track, the active segment raised
// white. Used for the top section nav and the Vocabulary level filter so
// they read as the same control.
function SegmentedControl<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string
  options: ReadonlyArray<{ id: T; label: string }>
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="inline-flex rounded-xl bg-slate-100 p-1">
      {options.map((option) => {
        const active = value === option.id
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.id)}
            className={`cursor-pointer rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function categoryPillClass(active: boolean): string {
  return `shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
    active
      ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700'
      : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
  }`
}

// A "pinned All + scrolling pills" row: "All" stays put outside the scroller
// (its own always-reachable option), the rest scroll horizontally under a
// gradient mask that fades whichever edge has more off-screen. `value` is
// 'all' or an option id; clicking the active pill toggles back to 'all'.
// Used for the Vocabulary category filter and the Reference pattern picker.
function PinnedAllPills({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string
  options: ReadonlyArray<{ id: string; label: string }>
  value: string
  onChange: (id: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ atStart: true, atEnd: true })
  const sync = () => {
    const el = scrollRef.current
    if (!el) return
    setEdges({ atStart: el.scrollLeft <= 1, atEnd: el.scrollLeft >= el.scrollWidth - el.clientWidth - 1 })
  }
  useEffect(() => {
    sync()
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => observer.disconnect()
  }, [options])
  const fadeStart = edges.atStart ? '0px' : '28px'
  const fadeEnd = edges.atEnd ? '0px' : '28px'
  const mask = `linear-gradient(to right, transparent 0, #000 ${fadeStart}, #000 calc(100% - ${fadeEnd}), transparent 100%)`
  return (
    <div className="flex items-start gap-2">
      <button type="button" aria-pressed={value === 'all'} onClick={() => onChange('all')} className={categoryPillClass(value === 'all')}>
        All
      </button>
      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={sync}
          aria-label={ariaLabel}
          className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ maskImage: mask, WebkitMaskImage: mask }}
        >
          {options.map((option) => {
            const active = value === option.id
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={active}
                onClick={() => onChange(active ? 'all' : option.id)}
                className={categoryPillClass(active)}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        {/* Overlaid chevrons — the mask fade alone is too subtle to signal
            "more options this way", so spell it out with an icon. */}
        {!edges.atEnd && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pb-1">
            <span className="flex size-5 items-center justify-center rounded-full bg-white/90 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/90 dark:ring-slate-700">
              <ChevronIcon direction="right" className="size-3.5 text-slate-400 dark:text-slate-500" />
            </span>
          </div>
        )}
        {!edges.atStart && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pb-1">
            <span className="flex size-5 items-center justify-center rounded-full bg-white/90 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/90 dark:ring-slate-700">
              <ChevronIcon direction="left" className="size-3.5 text-slate-400 dark:text-slate-500" />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// False friends — Portuguese words that look/sound like an English word but
// mean something else. Its own view on the Vocabulary tab (reached from the
// sub-tab), since it isn't levelled or categorised like the word lists.
function FalseFriendsReference({ playingKey, onPlay }: { playingKey: string | null; onPlay: (key: string, text: string) => void }) {
  const speakableForms = (value: string, keyBase: string) =>
    value.split(' / ').map((form, index) => (
      <span key={`${keyBase}:${index}`} className="inline-flex items-baseline">
        {index > 0 && <span aria-hidden="true" className="mx-0.5 text-slate-300">/</span>}
        <SpeakableWord text={form} speakAs={speechForm(form)} wrap playKey={`${keyBase}:${index}`} playingKey={playingKey} onPlay={onPlay} />
      </span>
    ))

  return (
    <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="w-[15%] px-4 py-3 font-semibold">Portuguese</th>
              <th className="w-[11%] px-4 py-3 font-semibold">English look-alike</th>
              <th className="w-[27%] px-4 py-3 font-semibold">English look-alike in Portuguese</th>
              <th className="px-4 py-3 font-semibold">The trap</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {FALSE_FRIENDS.map((friend) => (
              <tr key={friend.pt}>
                <td className="px-4 py-3 align-top">
                  <span className="flex flex-wrap items-baseline gap-y-1">{speakableForms(friend.pt, `ff:${friend.pt}:pt`)}</span>
                  <span className="mt-0.5 block text-slate-500">{friend.ptMeans}</span>
                </td>
                <td className="px-4 py-3 align-top">
                  <span className="text-slate-700">
                    <span aria-hidden="true" className="text-slate-400">≠ </span>
                    <span className="font-medium">{friend.soundsLike}</span>
                  </span>
                </td>
                <td className="px-4 py-3 align-top">
                  <span className="flex flex-wrap items-baseline gap-y-1">{speakableForms(friend.enInPt, `ff:${friend.pt}:en`)}</span>
                  <span className="mt-0.5 block text-slate-500">{friend.enMeans}</span>
                </td>
                <td className="px-4 py-3 align-top text-slate-500">{friend.trap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  )
}

// ---------------------------------------------------------------------------
// Transitions — the little words that join ideas when Brazilians talk
// ---------------------------------------------------------------------------

function TransitionsReference({ playingKey, onPlay }: { playingKey: string | null; onPlay: (key: string, text: string) => void }) {
  const speak = (text: string, key: string) => (
    <SpeakableWord text={text} speakAs={speechForm(text)} wrap playKey={key} playingKey={playingKey} onPlay={onPlay} />
  )

  return (
    <>
      <p className="mt-5 text-right text-sm text-slate-500">
        <span aria-hidden="true" className="font-semibold text-amber-600">★</span> learn these first
      </p>

      {TRANSITION_GROUPS.map((group) => {
        const rows = TRANSITIONS.filter((transition) => transition.group === group.id)
        return (
          <article key={group.id} className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
            <CardHeader title={group.label} />
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-left text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="w-[24%] px-4 py-2.5 font-semibold">Term</th>
                    <th className="w-[26%] px-4 py-2.5 font-semibold">English</th>
                    <th className="px-4 py-2.5 font-semibold">Example</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((transition) => (
                    <tr key={transition.term}>
                      <th scope="row" className="px-4 py-2 text-left align-top font-medium">
                        <span translate="no" className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          {speak(transition.term, `tr:${transition.term}`)}
                          {transition.mustKnow && <span role="img" aria-label="Must know" className="text-amber-600">★</span>}
                        </span>
                      </th>
                      <td className="px-4 py-2 align-top text-slate-700">
                        {transition.gloss}
                        {transition.note && <span className="mt-1 block text-xs text-slate-400">{transition.note}</span>}
                      </td>
                      <td translate="no" className="px-4 py-2 align-top">
                        <span className="-ml-1.5 block">{speak(transition.example, `tr-ex:${transition.term}`)}</span>
                        <span translate="yes" className="mt-0.5 block text-xs text-slate-400">{transition.exampleEn}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        )
      })}
    </>
  )
}

const VOCAB_VIEWS = [
  { id: 'words', label: 'Word lists' },
  { id: 'transitions', label: 'Transitions' },
  { id: 'falseFriends', label: 'False friends' },
] as const
type VocabView = (typeof VOCAB_VIEWS)[number]['id']

function VocabularyView() {
  // Sub-tab: the levelled/categorised word lists, or the False friends
  // reference (which is neither, so it gets its own view).
  const [view, setView] = useState<VocabView>('words')
  // Two independent filters that combine. Level values come from the data
  // (VOCAB_LEVELS) — adding B1 later needs no change here.
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [playingKey, setPlayingKey] = useState<string | null>(null)

  const playWord = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }

  const rows = VOCABULARY.filter(
    (row) => (levelFilter === 'all' || row.level === levelFilter) && (categoryFilter === 'all' || row.category === categoryFilter),
  )
  const showLevelColumn = levelFilter === 'all'
  const sections = VOCAB_CATEGORIES.map((category) => ({
    category,
    label: VOCAB_CATEGORY_LABEL[category],
    entries: rows.filter((row) => row.category === category),
  })).filter((section) => section.entries.length > 0)

  const isWords = view === 'words'
  const isTransitions = view === 'transitions'

  return (
    <main className={`mx-auto ${isWords ? 'max-w-4xl' : 'max-w-6xl'} px-4 py-6 sm:py-10`}>
      <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-700">Vocabulary</p>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">{isWords ? 'Words to Know' : isTransitions ? 'Transitions' : 'False friends'}</h1>
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
          {isWords
            ? `${rows.length} ${rows.length === 1 ? 'word' : 'words'}`
            : isTransitions
              ? `${TRANSITIONS.length} terms`
              : `${FALSE_FRIENDS.length} words`}
        </p>
      </div>
      <p className="mt-2 text-sm text-slate-500">
        {isWords
          ? 'The most common words and phrases to get by in daily situations.'
          : isTransitions
            ? 'The little words that join ideas when Brazilians talk, grouped by what they do.'
            : 'Portuguese words that look or sound like an English word but mean something else.'}
      </p>

      <div className="mt-5">
        <SegmentedControl ariaLabel="Vocabulary view" options={VOCAB_VIEWS} value={view} onChange={setView} />
      </div>

      {isTransitions ? (
        <TransitionsReference playingKey={playingKey} onPlay={playWord} />
      ) : !isWords ? (
        <FalseFriendsReference playingKey={playingKey} onPlay={playWord} />
      ) : (
        <>
          <div className="mt-5 space-y-4">
            <FilterRow label="Level">
              <SegmentedControl ariaLabel="Filter by level" options={VOCAB_LEVEL_SEGMENTS} value={levelFilter} onChange={setLevelFilter} />
            </FilterRow>
            <FilterRow label="Category">
              <PinnedAllPills
                ariaLabel="Filter by category"
                options={VOCAB_CATEGORIES.map((category) => ({ id: category, label: VOCAB_CATEGORY_LABEL[category] }))}
                value={categoryFilter}
                onChange={setCategoryFilter}
              />
            </FilterRow>
          </div>

          {sections.length === 0 ? (
            <p className="mt-6 rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-900">No vocabulary matches this filter yet.</p>
          ) : (
            sections.map((section) => (
              <article key={section.category} className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
                <CardHeader title={section.label} />
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[40rem] text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="w-1/5 px-4 py-2.5 font-semibold">English</th>
                        <th className="w-1/4 px-4 py-2.5 font-semibold">Portuguese</th>
                        {showLevelColumn && <th className="px-4 py-2.5 font-semibold">Level</th>}
                        <th className="px-4 py-2.5 font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {section.entries.map((row) => {
                        const forms = row.portuguese.split(' / ')
                        return (
                          <tr key={row.id}>
                            <th scope="row" className="px-4 py-2 text-left align-top font-medium text-slate-700">{row.english}</th>
                            <td className="px-4 py-2 align-top">
                              <span className="flex flex-wrap items-baseline justify-start gap-y-1">
                                {forms.map((form, formIndex) => (
                                  <span key={`${row.id}:${formIndex}`} className="inline-flex items-baseline">
                                    {formIndex > 0 && <span aria-hidden="true" className="mx-0.5 text-slate-300">/</span>}
                                    <SpeakableWord
                                      text={form}
                                      article={Array.isArray(row.article) ? row.article[formIndex] : row.article}
                                      speakAs={speechForm(form)}
                                      wrap
                                      playKey={`${row.id}:${formIndex}`}
                                      playingKey={playingKey}
                                      onPlay={playWord}
                                    />
                                  </span>
                                ))}
                              </span>
                            </td>
                            {showLevelColumn && (
                              <td className="px-4 py-2 align-top">
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-500">{row.level}</span>
                              </td>
                            )}
                            <td className="px-4 py-2 align-top text-slate-500">{row.notes}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </article>
            ))
          )}
        </>
      )}
    </main>
  )
}

// ---------------------------------------------------------------------------
// Translate — word/phrase lookup (EN ↔ PT), see the Translate & Lookup PRD.
// No LLM or translation API: everything renders from dictionary.ts's search
// over the small seed dataset in data/dictionary.json, plus infrastructure
// this file already has (SpeakableWord for gender + pronunciation,
// ConjugationTable for verbs).
// ---------------------------------------------------------------------------

// Distinct Portuguese headwords in the dictionary (a word with several senses
// counts once); senses is the raw entry count, so "casa" is one word but
// "laranja" (colour + fruit) is one word across two senses.
const DICTIONARY_WORD_COUNT = new Set(DICTIONARY.map((sense) => sense.pt.toLocaleLowerCase('pt'))).size
const DICTIONARY_SENSE_COUNT = DICTIONARY.length

const POS_LABEL: Record<string, string> = {
  noun: 'noun',
  verb: 'verb',
  adjective: 'adjective',
  adverb: 'adverb',
  pronoun: 'pronoun',
  preposition: 'preposition',
  article: 'article',
  conjunction: 'conjunction',
  interjection: 'interjection',
  phrase: 'phrase',
}

function articleFor(gender?: 'm' | 'f'): string | undefined {
  return gender === 'm' ? 'o' : gender === 'f' ? 'a' : undefined
}

// A trailing "(also: …)" in a gloss is a search alias for synonyms that
// have no sense of their own (home→casa, large→grande). Keep it in the data
// so lookup still finds them, but don't show it in the card.
function displayGloss(gloss: string): string {
  return gloss.replace(/\s*\(also:[^)]*\)\s*$/i, '').trim()
}

// One "possible results" list, reused verbatim for both an ambiguous match
// (PRD 5.4) and a no-exact-match "did you mean" suggestion list (PRD 5.5) —
// same component, different heading and trigger.
function PossibleResultsPanel({
  heading,
  groups,
  selectedKey,
  onSelect,
  playingKey,
  onPlay,
}: {
  heading: string
  groups: ResultGroup[]
  selectedKey: string
  onSelect: (key: string) => void
  playingKey: string | null
  onPlay: (key: string, text: string) => void
}) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 sm:p-5">
      <p className="text-sm font-semibold text-slate-500">{heading}</p>
      <ul className="mt-2 divide-y divide-slate-100">
        {groups.map((group) => {
          const primary = group.senses[0]
          const otherField = group.field === 'pt' ? 'en' : 'pt'
          return (
            <li key={group.key}>
              {/* A real <button> can't nest another button (the SpeakableWord
                  speaker icons below), so the row itself is a role="button"
                  div — selectable and keyboard-operable without that conflict. */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelect(group.key)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onSelect(group.key)
                }}
                aria-pressed={group.key === selectedKey}
                className={`flex w-full cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-slate-50 ${group.key === selectedKey ? 'bg-purple-50' : ''}`}
              >
                {group.field === 'pt' ? (
                  <SpeakableWord text={group.text} article={articleFor(primary.gender)} playKey={`poss:${group.key}`} playingKey={playingKey} onPlay={onPlay} />
                ) : (
                  <span className="font-semibold text-slate-950">{group.text}</span>
                )}
                <span aria-hidden="true" className="text-slate-400">→</span>
                {otherField === 'pt' ? (
                  <SpeakableWord text={primary.pt} article={articleFor(primary.gender)} playKey={`poss-to:${group.key}`} playingKey={playingKey} onPlay={onPlay} />
                ) : (
                  <span className="text-slate-700">{primary.en}</span>
                )}
                <span className="text-xs text-slate-400">{displayGloss(primary.gloss)}</span>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function TranslateResultCard({
  group,
  playingKey,
  onPlay,
  showTu,
  onToggleTu,
}: {
  group: ResultGroup
  playingKey: string | null
  onPlay: (key: string, text: string) => void
  showTu: boolean
  onToggleTu: () => void
}) {
  const [showConjugation, setShowConjugation] = useState(false)
  const primary = group.senses[0]
  const queriedPt = group.field === 'pt'
  // Any verb sense's headword is its infinitive; show the table whenever we can
  // build one for it (verbs.json, the irregular list, or a regular pattern) —
  // not only for the ~54 senses explicitly linked to verbs.json.
  const verbSense = group.senses.find((sense) => sense.pos === 'verb')
  const verbInfinitive = verbSense?.infinitive ?? verbSense?.pt
  const conjugationInfinitive = verbInfinitive && conjugationGridFor(verbInfinitive) ? verbInfinitive : undefined

  // The Portuguese side of a word: a plain speakable word, or "novo / nova"
  // when a sense carries a feminine form. `feminineOverride` lets the card
  // headword show the split even when the top-ranked sense is an invariant
  // noun (e.g. "o português" the language vs the adjective português/portuguesa).
  const ptForms = (sense: DictSense, keyBase: string, feminineOverride?: string) => {
    const feminine = feminineOverride ?? (sense.pos === 'adjective' ? sense.feminine : undefined)
    return feminine ? (
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <SpeakableWord text={sense.pt} playKey={`${keyBase}:m`} playingKey={playingKey} onPlay={onPlay} />
        <span aria-hidden="true" className="text-xs font-normal text-slate-400">(m)</span>
        <span aria-hidden="true" className="text-slate-300">/</span>
        <SpeakableWord text={feminine} playKey={`${keyBase}:f`} playingKey={playingKey} onPlay={onPlay} />
        <span aria-hidden="true" className="text-xs font-normal text-slate-400">(f)</span>
      </span>
    ) : (
      <SpeakableWord text={sense.pt} article={articleFor(sense.gender)} playKey={keyBase} playingKey={playingKey} onPlay={onPlay} />
    )
  }
  const groupFeminine = group.senses.find((sense) => sense.feminine)?.feminine
  // When a card bundles senses of different parts of speech (bem: adverb +
  // interjection), tag every sense inline — not just the ones that differ
  // from the header badge — so the labeling reads the same way top to bottom.
  const hasMixedPos = group.senses.some((sense) => sense.pos !== primary.pos)

  return (
    <article className="mt-6 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 [&_tbody>tr:last-child>*]:pb-3">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-2xl">
            {queriedPt ? (
              ptForms(primary, `head:${group.key}`, groupFeminine)
            ) : (
              <span className="font-bold text-slate-950">{primary.en}</span>
            )}
          </div>
          <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-widest text-blue-800">{POS_LABEL[primary.pos]}</span>
        </div>
        {group.viaForm && (
          <p className="mt-1 text-xs text-slate-400">
            <span translate={queriedPt ? 'no' : undefined} className="italic text-slate-500">{group.viaForm}</span>{' '}
            {group.viaKind === 'plural' ? 'is the plural of' : 'is a conjugated form of'}{' '}
            <span translate={queriedPt ? 'no' : undefined}>{queriedPt ? primary.pt : primary.en}</span>
          </p>
        )}
      </div>

      {/* Every sense, numbered like a dictionary: (context) · translation · example.
          Ordered by frequency, so #1 is the most common way to use the word.
          A sense's usage note sits at the top of its own row, so it reads
          against the right word when a card has several senses. */}
      <ol className="divide-y divide-slate-100">
        {group.senses.map((sense, index) => (
          <li key={sense.id} className="px-5 py-4">
            {sense.note && (
              <div className="mb-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <span className="mt-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-700">Note</span>
                <span>{sense.note}</span>
              </div>
            )}
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-semibold text-slate-400">{index + 1}.</span>
              <span className="text-sm text-emerald-700">({displayGloss(sense.gloss)})</span>
              {hasMixedPos && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{POS_LABEL[sense.pos]}</span>
              )}
            </div>
            <div className="mt-1 pl-6 text-lg">
              {queriedPt ? (
                <span className="font-semibold text-slate-900">{sense.en}</span>
              ) : (
                ptForms(sense, `sense:${group.key}:${sense.id}`)
              )}
            </div>
            {sense.example && (
              <p className="mt-1.5 pl-6 text-sm leading-relaxed">
                <SpeakableWord text={sense.example.pt} playKey={`ex:${group.key}:${sense.id}`} playingKey={playingKey} onPlay={onPlay} />
                <span className="text-slate-300"> — </span>
                <span className="text-slate-500">{sense.example.en}</span>
              </p>
            )}
          </li>
        ))}
      </ol>

      {conjugationInfinitive && (
        <div className="border-t border-slate-100 px-5 py-4">
          <button type="button" onClick={() => setShowConjugation((value) => !value)} className="cursor-pointer text-sm font-semibold text-purple-700 hover:text-purple-800">
            {showConjugation ? 'Hide' : 'Show'} conjugation table (<span translate="no">{conjugationInfinitive}</span>)
          </button>
          {showConjugation && (
            <div className="mt-3">
              <label className="mb-3 flex w-fit cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-xs ring-1 ring-slate-200 hover:ring-slate-300">
                <input type="checkbox" checked={showTu} onChange={onToggleTu} className="size-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="font-medium text-slate-700">Show tu</span>
              </label>
              <ConjugationTable infinitive={conjugationInfinitive} showTu={showTu} playingKey={playingKey} onPlay={onPlay} />
            </div>
          )}
        </div>
      )}

    </article>
  )
}

// Word/phrase lookup is not a sentence translator (PRD non-goal). Sentence-
// length input gets a clear message plus an optional, clearly-labelled
// word-by-word breakdown — never presented as if it were a translation.
function SentenceFallback({
  query,
  showBreakdown,
  onToggleBreakdown,
  onPlay,
}: {
  query: string
  showBreakdown: boolean
  onToggleBreakdown: () => void
  onPlay: (key: string, text: string) => void
}) {
  const breakdown = showBreakdown ? wordByWordBreakdown(query) : []
  return (
    <div className="mt-6 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p>Looks like a full sentence — try a single word for now.</p>
      <button type="button" onClick={onToggleBreakdown} className="mt-2 cursor-pointer font-semibold underline underline-offset-2">
        {showBreakdown ? 'Hide' : 'Show'} a word-by-word breakdown
      </button>
      {showBreakdown && (
        <>
          <p className="mt-2 text-xs text-amber-800">Word-by-word only — not a sentence translation, and it won't reflect word order, idioms, or grammar.</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {breakdown.map((entry, index) => {
              const sense = entry.group?.senses[0]
              const gloss = sense ? (entry.group!.field === 'pt' ? sense.en : sense.pt) : null
              return (
                <li key={`${entry.word}-${index}`} className="rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-amber-200">
                  <span translate={entry.group?.field === 'pt' ? 'no' : undefined} className="font-semibold text-slate-800">{entry.word}</span>
                  <span translate={entry.group?.field === 'en' ? 'no' : undefined} className={gloss ? 'text-slate-500' : 'text-slate-400'}> — {gloss ?? 'not found'}</span>
                  {sense && (
                    <button
                      type="button"
                      onClick={() => onPlay(`bw:${index}`, entry.group!.field === 'pt' ? entry.word : sense.pt)}
                      aria-label={`Play pronunciation of ${entry.group!.field === 'pt' ? entry.word : sense.pt}`}
                      className="ml-1.5 cursor-pointer align-middle text-blue-600 hover:text-blue-700"
                    >
                      <SpeakerWaveOutlineIcon className="inline size-3.5" />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}

type RecentSearch = { pt: string; en: string }
const RECENT_SEARCHES_KEY = 'translate-recent-searches'
const RECENT_SEARCHES_LIMIT = 7

// A handful of unique words to suggest before the learner has searched
// anything (and to top up recent history while it's still short of the
// limit). Sampled once per page load, not re-rolled on every render.
function pickRandomWords(count: number): DictSense[] {
  const seen = new Set<string>()
  const pool: DictSense[] = []
  for (const sense of DICTIONARY) {
    const key = sense.pt.toLocaleLowerCase('pt')
    if (seen.has(key)) continue
    seen.add(key)
    pool.push(sense)
  }
  const picked: DictSense[] = []
  for (let i = 0; i < count && pool.length > 0; i++) {
    picked.push(...pool.splice(Math.floor(Math.random() * pool.length), 1))
  }
  return picked
}

function TranslateView() {
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])
  const [randomWords] = useState<DictSense[]>(() => pickRandomWords(RECENT_SEARCHES_LIMIT))
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [playingKey, setPlayingKey] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [showTu, setShowTu] = useState(false)
  const [showBreakdown, setShowBreakdown] = useState(false)

  const suggestions: AutocompleteCandidate[] = query.trim() ? autocomplete(query) : []
  const result = submitted ? lookup(submitted) : null
  const dropdownOpen = showDropdown && suggestions.length > 0
  const optionId = (index: number) => `translate-ac-option-${index}`

  const playWord = (key: string, text: string) => {
    setPlayingKey(key)
    pronounceForm(text, () => setPlayingKey((current) => (current === key ? null : current)))
  }

  function runSearch(value: string) {
    const trimmed = value.trim()
    setQuery(trimmed)
    setSubmitted(trimmed)
    setSelectedKey(null)
    setShowDropdown(false)
    setActiveIndex(-1)
    setShowBreakdown(false)
  }

  const groups = result && (result.type === 'match' || result.type === 'suggestions') ? result.groups : []
  const selectedGroup = groups.find((group) => group.key === selectedKey) ?? groups[0] ?? null
  const showPanel = result?.type === 'suggestions' || (result?.type === 'match' && result.ambiguous) || false
  const panelHeading = result?.type === 'suggestions' ? `Couldn't find an exact match for "${result.query}" — did you mean:` : 'Possible results'

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
      if (raw) setRecentSearches(JSON.parse(raw))
    } catch {
      // Storage may be unavailable (private browsing, disabled) — just start empty.
    }
  }, [])

  // Record every successful lookup as recent history, newest first, so
  // revisiting the tab reinforces words the learner actually searched for.
  useEffect(() => {
    if (result?.type !== 'match' || !selectedGroup) return
    const sense = selectedGroup.senses[0]
    const entry: RecentSearch = { pt: sense.pt, en: sense.en }
    setRecentSearches((current) => {
      const deduped = current.filter((item) => item.pt.toLocaleLowerCase('pt') !== entry.pt.toLocaleLowerCase('pt'))
      const next = [entry, ...deduped].slice(0, RECENT_SEARCHES_LIMIT)
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next))
      } catch {
        // Ignore write failures — history just won't persist this session.
      }
      return next
    })
  }, [result?.type, selectedGroup?.key])

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 sm:py-10">
      <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-700">Translate</p>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">Word Lookup</h1>
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">{DICTIONARY_WORD_COUNT} words · {DICTIONARY_SENSE_COUNT} senses</p>
      </div>
      <p className="mt-1 text-sm text-slate-500">High-frequency words, and a running list of words I'm learning.</p>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          runSearch(query)
        }}
        className="relative mt-5"
      >
        <input
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setShowDropdown(true)
            setActiveIndex(-1)
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 120)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              if (!dropdownOpen) {
                setShowDropdown(true)
                return
              }
              setActiveIndex((index) => (index + 1) % suggestions.length)
            } else if (event.key === 'ArrowUp') {
              if (!dropdownOpen) return
              event.preventDefault()
              setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1))
            } else if (event.key === 'Enter') {
              if (dropdownOpen && activeIndex >= 0 && activeIndex < suggestions.length) {
                event.preventDefault()
                runSearch(suggestions[activeIndex].text)
              }
            } else if (event.key === 'Escape') {
              setShowDropdown(false)
              setActiveIndex(-1)
            }
          }}
          placeholder="Type words in English or Portuguese"
          aria-label="Search for a word or phrase"
          role="combobox"
          aria-expanded={dropdownOpen}
          aria-controls="translate-ac-listbox"
          aria-autocomplete="list"
          aria-activedescendant={dropdownOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-lg text-slate-950 placeholder-[#9CA3AF] focus:border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-600"
        />
        {dropdownOpen && (
          <ul id="translate-ac-listbox" role="listbox" className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200">
            {suggestions.map((candidate, index) => (
              <li key={`${candidate.field}:${candidate.text}`} role="option" id={optionId(index)} aria-selected={index === activeIndex}>
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runSearch(candidate.text)}
                  className={`flex w-full cursor-pointer items-baseline gap-4 px-4 py-2.5 text-left ${index === activeIndex ? 'bg-purple-50' : ''}`}
                >
                  <span translate={candidate.field === 'pt' ? 'no' : undefined} className="w-36 shrink-0 truncate font-medium text-slate-900">{candidate.text}</span>
                  <span translate={candidate.field === 'en' ? 'no' : undefined} className="min-w-0 flex-1 truncate text-sm text-slate-500">{candidate.translation}</span>
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-400">{candidate.field}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>

      {!result && (
        <div className="mt-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {recentSearches.length > 0 ? 'Recently looked up' : 'Try one of these'}
          </p>
          <div className="flex flex-wrap gap-2">
            {(recentSearches.length > 0 ? recentSearches : randomWords).map((entry) => (
              <button
                key={entry.pt}
                type="button"
                onClick={() => runSearch(entry.pt)}
                className="cursor-pointer rounded-full bg-white px-4 py-2 text-sm ring-1 ring-slate-200 transition-colors hover:ring-slate-300"
              >
                <span translate="no" className="font-semibold text-slate-900">{entry.pt}</span>
                <span className="ml-2 text-slate-500">{entry.en}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {result?.type === 'sentence' && (
        <SentenceFallback query={submitted} showBreakdown={showBreakdown} onToggleBreakdown={() => setShowBreakdown((value) => !value)} onPlay={playWord} />
      )}

      {result?.type === 'none' && (
        <p role="status" className="mt-6 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No results found for &ldquo;{result.query}&rdquo;.
        </p>
      )}

      {showPanel && selectedGroup && (
        <div className="mt-6">
          <PossibleResultsPanel heading={panelHeading} groups={groups} selectedKey={selectedGroup.key} onSelect={setSelectedKey} playingKey={playingKey} onPlay={playWord} />
        </div>
      )}

      {selectedGroup && (
        <TranslateResultCard
          key={selectedGroup.key}
          group={selectedGroup}
          playingKey={playingKey}
          onPlay={playWord}
          showTu={showTu}
          onToggleTu={() => setShowTu((value) => !value)}
        />
      )}

    </main>
  )
}

// About — a page about the maker, not the app. Caderno Brasil is framed here
// as one of several curiosity-driven learning tools Jey has built, not the
// whole identity — so the heading and kicker are about jeyfala, not the app.
// `onNavigate` lets the section chips jump straight to another tab.
function AboutView({ onNavigate }: { onNavigate: (event: ReactMouseEvent, tab: Tab) => void }) {
  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-700">About</p>
      <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">About jeyfala</h1>
      <p className="mt-2 text-sm text-slate-500">A learning practice, shared publicly</p>

      {/* Hero: portrait + enlarged lead paragraph, stacking on mobile. */}
      <div className="mt-8 flex flex-col items-center gap-6 sm:flex-row sm:items-center">
        <div className="relative h-36 w-36 shrink-0 sm:h-40 sm:w-40">
          <div aria-hidden="true" className="absolute inset-0 translate-x-2 translate-y-2 rounded-full bg-amber-200" />
          <img
            src="/jey-portrait.png"
            alt="Illustrated portrait of Jey"
            className="relative h-full w-full rounded-full object-cover ring-4 ring-white"
          />
        </div>
        <p className="text-center font-serif text-xl leading-snug text-slate-950 sm:text-left sm:text-2xl">
          <span className="font-bold text-purple-700">Olá, I'm Jey.</span>
          <br />
          <span className="font-semibold">jeyfala is my home base for learning Brazilian Portuguese.</span>
        </p>
      </div>

      {/* Pull quote. */}
      <div className="mt-10 flex items-start gap-3">
        <span aria-hidden="true" className="font-serif text-5xl leading-none text-amber-400">
          &ldquo;
        </span>
        <p className="mt-1 text-lg leading-relaxed text-slate-700">
          <span className="font-serif font-semibold text-slate-950">This notebook grows as I learn.</span>{' '}
          <span className="text-base text-slate-500">
            New words, practice exercises, and songs keep finding their way here. Come back and see what's changed.
          </span>
        </p>
      </div>

      {/* How-it-started / find-here pair, side by side on desktop. */}
      <div className="mt-10 grid gap-8 border-t border-slate-200 pt-8 sm:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-purple-700">How this started</p>
          <p className="mt-2 text-base leading-relaxed text-slate-700">
            Learning Portuguese was never part of my plan, but in the summer of 2026, the universe kept sending little signals that
            nudged me to commit. So I started, and this notebook, Caderno Brasil, was born.
          </p>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-purple-700">What you'll find here</p>
          <p className="mt-2 text-base leading-relaxed text-slate-700">
            Caderno Brasil brings together everything I'm working on around Brazilian Portuguese and culture, including a growing
            library of capoeira songs I'm practicing.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {NAV_TABS.filter((item) => item.id !== 'about').map((item) => (
              <a
                key={item.id}
                href={TAB_PATH[item.id]}
                onClick={(event) => onNavigate(event, item.id)}
                className="cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Closing + CTA, sharing one row on wide screens. */}
      <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-base leading-relaxed text-slate-700">
          I'm learning Brazilian Portuguese one conversation, song, and mistake at a time. This notebook makes my progress visible,
          for me and for anyone learning alongside me. It reflects where I am now, so it will keep changing as I do.
        </p>
        <a
          href="https://www.instagram.com/jeyfala_/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 self-start rounded-xl bg-purple-700 px-5 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-purple-800 sm:self-auto"
        >
          <InstagramIcon className="size-5" />
          Say oi
        </a>
      </div>

      {/* A note on level — full width, its own section, last. */}
      <div className="mt-10 border-t border-slate-200 pt-8">
        <p className="text-xs font-bold uppercase tracking-widest text-purple-700">A note on level</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          I came to Portuguese having learned Spanish years ago, so these tools may jump past the absolute basics to the questions I
          have. If you're starting from zero, think of this as a companion rather than a step-by-step course.
        </p>
      </div>
    </main>
  )
}

// Songs — the capoeira songbook. One shared player at the top (video first,
// recording pills and a lyrics toggle right under it); the song list below
// just loads a song into that player. A slim mini-player docks at the top of
// the screen once the main player scrolls out of view.
const YT_ORIGIN = 'https://www.youtube-nocookie.com'

function scrollToElement(element: HTMLElement | null) {
  if (!element) return
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  element.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
}

function PlaySolidIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M8 5.14v13.72a1 1 0 0 0 1.52.85l11.1-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14Z" />
    </svg>
  )
}

function PauseSolidIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M7 4.5A1.5 1.5 0 0 1 8.5 3h1A1.5 1.5 0 0 1 11 4.5v15A1.5 1.5 0 0 1 9.5 21h-1A1.5 1.5 0 0 1 7 19.5v-15Zm6 0A1.5 1.5 0 0 1 14.5 3h1A1.5 1.5 0 0 1 17 4.5v15a1.5 1.5 0 0 1-1.5 1.5h-1A1.5 1.5 0 0 1 13 19.5v-15Z" />
    </svg>
  )
}

function SongsView() {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const filtered = SONGS.filter((song) => matchesSongQuery(song, query))

  const [song, setSong] = useState<Song>(SONGS[0])
  const [recordingUrl, setRecordingUrl] = useState<string | null>(() => firstEmbeddableRecording(SONGS[0])?.url ?? null)
  // The first video loads paused; anything the learner picks afterwards autoplays.
  const [userStarted, setUserStarted] = useState(false)
  const [lyricsOpen, setLyricsOpen] = useState(false)
  // Whether the mounted iframe has fired onLoad yet — drives the loading spinner.
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [pastPlayer, setPastPlayer] = useState(false)
  const playerRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const recording = song.recordings.find((candidate) => candidate.url === recordingUrl)
  const embedUrl = recordingUrl
    ? youTubeEmbedUrl(recordingUrl, { autoplay: userStarted, jsApi: true, origin: window.location.origin })
    : null

  function sendToPlayer(message: object) {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(message), YT_ORIGIN)
  }

  function loadRecording(nextSong: Song, nextUrl: string | null) {
    if (nextSong === song && nextUrl === recordingUrl && userStarted) return
    setSong(nextSong)
    setRecordingUrl(nextUrl)
    setUserStarted(true)
    setVideoLoaded(false)
    setPlaying(false)
  }

  // YouTube reports play/pause through postMessage once we say we're listening
  // (sent from the iframe's onLoad). That's what keeps the mini-player's
  // play/pause icon honest.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== YT_ORIGIN || event.source !== iframeRef.current?.contentWindow) return
      let data: unknown = event.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          return
        }
      }
      const message = data as { event?: string; info?: unknown }
      if (message.event === 'onStateChange' && typeof message.info === 'number') {
        setPlaying(message.info === 1)
      } else if (message.event === 'infoDelivery') {
        const state = (message.info as { playerState?: unknown } | undefined)?.playerState
        if (typeof state === 'number') setPlaying(state === 1)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // The mini-player shows only once the video has scrolled off the top.
  useEffect(() => {
    const target = videoRef.current
    if (!target) return
    const observer = new IntersectionObserver(([entry]) => setPastPlayer(!entry.isIntersecting && entry.boundingClientRect.bottom < 0))
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  const miniVisible = pastPlayer && embedUrl !== null

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:py-10">
      <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-purple-700">Songs</p>
      <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">Capoeira Songs</h1>
      <p className="mt-2 text-sm text-slate-500">Lyrics, translations, and recordings for the songs I'm learning.</p>

      <div
        aria-hidden={!miniVisible}
        className={`fixed inset-x-0 top-0 z-30 flex items-center bg-slate-950 lg:left-60 text-white shadow-lg transition-all duration-200 ${
          miniVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
        }`}
      >
        <button
          type="button"
          tabIndex={miniVisible ? 0 : -1}
          onClick={() => scrollToElement(playerRef.current)}
          aria-label={`Back to the player: ${song.title}`}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2.5 pl-4 text-left"
        >
          <MusicNoteIcon className="size-4 shrink-0 text-white/70" />
          <span className="truncate text-sm font-semibold">{song.title}</span>
        </button>
        <button
          type="button"
          tabIndex={miniVisible ? 0 : -1}
          onClick={() => sendToPlayer({ event: 'command', func: playing ? 'pauseVideo' : 'playVideo', args: [] })}
          aria-label={playing ? 'Pause' : 'Play'}
          className="mr-2 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors hover:bg-white/15"
        >
          {playing ? <PauseSolidIcon className="size-5" /> : <PlaySolidIcon className="size-5" />}
        </button>
      </div>

      <section ref={playerRef} aria-label="Player" className="mt-6 scroll-mt-4">
        <div className="md:grid md:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)] md:gap-6 md:rounded-3xl md:bg-slate-800 md:p-5 md:shadow-lg">
          <div className="min-w-0">
            <p className="mb-2 truncate text-sm text-slate-500 md:hidden">
              Now playing: <span className="font-semibold text-slate-950">{song.title}</span>
            </p>
            <div ref={videoRef} className="relative aspect-video overflow-hidden rounded-xl bg-slate-950">
              {embedUrl ? (
                <>
                  {!videoLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <SpinnerIcon className="size-8 animate-spin text-slate-500" />
                    </div>
                  )}
                  <iframe
                    key={embedUrl}
                    ref={iframeRef}
                    src={embedUrl}
                    title={`${song.title} — ${recording?.label ?? 'recording'}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    onLoad={() => {
                      setVideoLoaded(true)
                      sendToPlayer({ event: 'listening', id: 1, channel: 'widget' })
                    }}
                    className="relative size-full border-0"
                  />
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-white/80">
                  <PlayCircleIcon className="size-10 text-white/50" />
                  <p className="text-sm">
                    {song.recordings.length === 0 ? 'Recording coming soon' : 'This recording opens in a new tab — tap it below.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex min-w-0 flex-col md:mt-0">
            <div className="hidden md:block">
              <p className="text-xs font-bold uppercase tracking-widest text-white/60">Now playing</p>
              <p translate="no" className="mt-1 font-serif text-2xl font-semibold leading-tight text-white">
                {song.title}
              </p>
            </div>

            <p className="mt-5 hidden text-xs font-bold uppercase tracking-widest text-white/60 md:block">Recordings</p>
            <div className="flex flex-wrap gap-2 md:mt-2">
              {song.recordings.length === 0 && <span className="py-2 text-sm text-slate-500 md:text-white/60">Recording coming soon</span>}
              {song.recordings.map((candidate) => {
                const embeddable = youTubeEmbedUrl(candidate.url) !== null
                const active = recordingUrl === candidate.url
                const pillClass = `inline-flex items-center rounded-full border px-3.5 py-1.5 text-left text-[13px] font-semibold transition-colors md:px-4 md:py-2 md:text-sm ${
                  active
                    ? 'border-purple-600 bg-purple-600 text-white'
                    : 'border-slate-200 bg-white text-slate-950 hover:bg-slate-50'
                }`
                if (!embeddable) {
                  return (
                    <a key={candidate.url} href={candidate.url} target="_blank" rel="noopener noreferrer" className={pillClass}>
                      {candidate.label}
                    </a>
                  )
                }
                return (
                  <button
                    key={candidate.url}
                    type="button"
                    onClick={() => loadRecording(song, candidate.url)}
                    aria-pressed={active}
                    className={`cursor-pointer ${pillClass}`}
                  >
                    {candidate.label}
                  </button>
                )
              })}
            </div>

            <div aria-hidden="true" className="hidden md:block md:min-h-4 md:flex-1" />
            <button
              type="button"
              onClick={() => setLyricsOpen((open) => !open)}
              aria-expanded={lyricsOpen}
              aria-controls="song-lyrics"
              className={`mt-4 flex w-full cursor-pointer items-center justify-center gap-2 border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition-colors md:mt-0 md:rounded-xl md:border-b md:border-white ${
                lyricsOpen ? 'rounded-t-2xl rounded-b-none border-b-0 md:hover:bg-slate-50' : 'rounded-xl hover:bg-slate-50'
              }`}
            >
              Lyrics
              <ChevronIcon direction={lyricsOpen ? 'up' : 'down'} className="size-4" />
            </button>
          </div>
        </div>

        {lyricsOpen && (
          <div
            id="song-lyrics"
            className="rounded-2xl rounded-t-none border border-t-0 border-slate-200 bg-white px-5 py-5 shadow-sm md:mt-4 md:rounded-t-2xl md:border-t"
          >
            {song.lyrics.length > 0 ? (
              <>
                <div className="hidden gap-6 sm:grid sm:grid-cols-2">
                  <p className="border-b border-slate-200 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Português</p>
                  <p className="border-b border-slate-200 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">English</p>
                </div>
                <div className="sm:mt-3">
                  {song.lyrics.map((block, index) => (
                    <div key={index} className="grid gap-1.5 border-t border-slate-100 pt-5 first:border-t-0 first:pt-0 sm:grid-cols-2 sm:gap-6 [&:not(:first-child)]:mt-5">
                      <p translate="no" className="whitespace-pre-line font-semibold leading-relaxed text-slate-950">
                        {block.pt}
                      </p>
                      <p className="whitespace-pre-line italic leading-relaxed text-slate-500">{block.en}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm italic text-slate-400">Lyrics coming soon.</p>
            )}
            {song.source && (
              <p className="mt-6 text-sm text-slate-500">
                Lyrics:{' '}
                <a
                  href={song.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline-offset-2 hover:text-blue-700 hover:underline"
                >
                  {song.source.label}
                </a>
              </p>
            )}
          </div>
        )}
      </section>

      <h2 className="mt-10 font-serif text-2xl font-semibold text-slate-950">All songs</h2>
      <div className="relative mt-4">
        <input
          id="songs-search"
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQuery('')
          }}
          placeholder="Search titles or Portuguese lyrics"
          aria-label="Search titles or Portuguese lyrics"
          autoComplete="off"
          className="w-full rounded-full border border-slate-200 bg-white px-5 py-3 pr-12 text-base text-slate-950 placeholder-[#9CA3AF] focus:border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-600"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="size-5" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8 rounded-2xl bg-amber-50 px-5 py-6 text-center">
          <p className="text-slate-700">
            Darn, I haven't learned that one yet. Message me on{' '}
            <a
              href="https://www.instagram.com/jeyfala_/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-purple-700 underline-offset-2 hover:underline"
            >
              Instagram
            </a>{' '}
            — maybe it'll be next!
          </p>
          <button
            type="button"
            onClick={() => setQuery('')}
            className="mt-4 cursor-pointer rounded-full bg-purple-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-800"
          >
            Show all songs
          </button>
        </div>
      ) : (
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {filtered.map((item) => {
            const current = item === song
            return (
              <li key={item.title}>
                <button
                  type="button"
                  onClick={() => {
                    loadRecording(item, firstEmbeddableRecording(item)?.url ?? null)
                    scrollToElement(playerRef.current)
                  }}
                  aria-label={`Play ${item.title}`}
                  aria-current={current ? 'true' : undefined}
                  className={`flex w-full cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                    current ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span translate="no" className="font-serif text-lg font-semibold leading-snug text-slate-950">
                      {item.title}
                    </span>
                    <span className="ml-2 whitespace-nowrap text-sm text-slate-500">
                      · {item.description}
                    </span>
                  </span>
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-purple-600 text-white">
                    <PlaySolidIcon className="size-5" />
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </main>
  )
}

type Tab = 'drills' | 'reference' | 'vocabulary' | 'translate' | 'songs' | 'about'
const NAV_TABS: ReadonlyArray<{ id: Tab; label: string; badge?: string }> = [
  { id: 'drills', label: 'Practice' },
  { id: 'reference', label: 'Grammar' },
  { id: 'vocabulary', label: 'Vocabulary' },
  { id: 'translate', label: 'Translate' },
  { id: 'songs', label: 'Songs', badge: 'New' },
  { id: 'about', label: 'About' },
]

// Each section's own indexable URL — kept in sync with config/routes.rb and
// HomeController::SECTIONS, which map these same paths back to a tab so a
// direct visit (or a crawler) lands on the right section server-side too.
const TAB_PATH: Record<Tab, string> = {
  drills: '/practice',
  reference: '/grammar',
  vocabulary: '/vocabulary',
  translate: '/translate',
  songs: '/songs',
  about: '/about',
}

// "/" isn't a key in TAB_PATH (nothing ever pushes it as a section's own
// URL), but it's still a real, distinct history entry — the initial page
// load — so the back/forward handler needs to resolve it too, to whatever
// tab the server actually rendered there (see HomeController::SECTIONS).
const PATH_TAB: Record<string, Tab> = {
  '/': 'about',
  ...(Object.fromEntries(Object.entries(TAB_PATH).map(([tab, path]) => [path, tab])) as Record<string, Tab>),
}

// Title/description per tab — kept in sync with HomeController::SECTIONS,
// which sets the same copy server-side for the initial HTML response.
const TAB_META: Record<Tab, { title: string; description: string }> = {
  drills: {
    title: 'Practice · Caderno Brasil',
    description: 'Practice Brazilian Portuguese with drills for verb conjugations, contractions, object pronouns, comparisons, and plurals.',
  },
  reference: {
    title: 'Grammar · Caderno Brasil',
    description: 'A Brazilian Portuguese grammar cheat sheet covering verb conjugations, contractions, object pronouns, comparisons, plurals, the subjunctive, nuances, and verb choice.',
  },
  vocabulary: {
    title: 'Vocabulary · Caderno Brasil',
    description: 'High-frequency Brazilian Portuguese vocabulary and a running list of words worth learning.',
  },
  translate: {
    title: 'Translate · Caderno Brasil',
    description: 'Look up English and Portuguese word translations, with example senses and usage.',
  },
  songs: {
    title: 'Capoeira Songs · Caderno Brasil',
    description: 'Lyrics, translations, and recordings for capoeira songs in Brazilian Portuguese.',
  },
  about: {
    title: 'About · Caderno Brasil',
    description: 'About Caderno Brasil, a personal notebook for learning Brazilian Portuguese.',
  },
}

export default function Home({ initialTab }: { initialTab: Tab }) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [topic, setTopic] = useState<Topic>('conjugation')
  const [screen, setScreen] = useState<Screen>('setup')
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [contractionIds, setContractionIds] = useState<string[]>(DEFAULT_CONTRACTION_IDS)
  const [contractionHint, setContractionHint] = useState<HintLevel>(DEFAULT_HINT_LEVEL)
  // Object-pronoun drill config. `opRegister` persists across sessions so the
  // drill defaults to the last register used; the app opens on Spoken, the
  // register a conversational learner practises most.
  const [opRegister, setOpRegister] = useState<Register>('spoken')
  const [opCategories, setOpCategories] = useState<OPCategory[]>(DEFAULT_OP_CATEGORY_IDS)
  const [opPlacements, setOpPlacements] = useState<Placement[]>(DEFAULT_OP_PLACEMENT_IDS)
  const [comparativeCategories, setComparativeCategories] = useState<ComparativeCategory[]>(INITIAL_COMPARATIVE_CATEGORY_IDS)
  const [comparativeForms, setComparativeForms] = useState<ComparativeForm[]>(INITIAL_COMPARATIVE_FORM_IDS)
  const [pluralCards, setPluralCards] = useState<PluralCardId[]>(DEFAULT_PLURAL_CARD_IDS)
  const [items, setItems] = useState<DrillItem[]>([])
  const [score, setScore] = useState<DrillScore | null>(null)
  // Reference-tab "Show tu" toggle and verb-type section — kept here so they
  // survive switching between verbs, topics, and the two tabs in a session
  // (and so the header can show the section-aware colour key).
  const [showTu, setShowTu] = useState(false)
  const [conjSection, setConjSection] = useState<ConjSection>('regular')

  // Chrome's page translator is more reliable about skipping Portuguese
  // content when an element carries both the translate="no" attribute AND
  // the legacy "notranslate" class (the older Google Website Translator
  // convention that some translate code paths still key off). Rather than
  // hand-adding that class at every one of the ~30 translate="no" call
  // sites — and keeping it in sync as they change — mirror it here once,
  // centrally, for every element that has translate="no" right now or ever
  // gains it as the drill/session content re-renders.
  useEffect(() => {
    const sync = () => {
      document.querySelectorAll('[translate="no"]:not(.notranslate)').forEach((el) => el.classList.add('notranslate'))
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['translate'] })
    return () => observer.disconnect()
  }, [])

  // Switching topic is a fresh start for that topic's config.
  function selectTopic(next: Topic) {
    setTopic(next)
    setScreen('setup')
    setScore(null)
  }

  // Plurals, Subjunctive, Nuances and Verb choice are Grammar-only topics; if it's still selected when the user
  // jumps to Practice, fall back to a topic that actually has a drill.
  function selectTab(next: Tab) {
    if (next === 'drills' && !TOPICS.some((entry) => entry.id === topic)) selectTopic('conjugation')
    setTab(next)
    if (window.location.pathname !== TAB_PATH[next]) window.history.pushState(null, '', TAB_PATH[next])
  }

  // The nav renders real <a href> links (for crawlers and open-in-new-tab),
  // but a normal click switches tabs client-side instead of reloading the
  // page — there's no new data to fetch from the server either way.
  function navigate(event: ReactMouseEvent, next: Tab) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    selectTab(next)
  }

  // Back/forward buttons: sync `tab` with whatever the URL now says, without
  // pushing a new history entry (the browser already moved for us).
  useEffect(() => {
    const onPopState = () => {
      const next = PATH_TAB[window.location.pathname]
      if (next) setTab(next)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  function start() {
    setItems(
      topic === 'conjugation'
        ? buildSession(settings)
        : topic === 'contraction'
          ? buildContractionItems(contractionIds, contractionHint)
          : topic === 'objectPronoun'
            ? buildObjectPronounItems(opCategories, opRegister, opPlacements)
            : topic === 'plurals'
              ? buildPluralItems(pluralCards)
              : buildComparativeItems(comparativeCategories, comparativeForms),
    )
    setScreen('drill')
  }

  if (screen === 'drill') {
    return (
      <Drill
        items={items}
        onExit={() => setScreen('setup')}
        onComplete={(result) => {
          setScore(result)
          setScreen('complete')
        }}
      />
    )
  }

  const complete = screen === 'complete'

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 lg:flex">
      <Head title={TAB_META[tab].title}>
        <meta head-key="description" name="description" content={TAB_META[tab].description} />
      </Head>

      {/* Slim sticky sidebar on desktop, with a solid dark pill behind the active tab. */}
      <aside className="hidden shrink-0 border-r border-slate-200 bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-60 lg:flex-col lg:justify-between lg:px-4 lg:py-8">
        <div>
          <div className="mb-8 px-3">
            <p className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-950">
              <span aria-hidden="true" className="text-2xl leading-none">🇧🇷</span> Caderno Brasil
            </p>
            <p className="mt-1 text-xs text-slate-500">Notes on learning Brazilian Portuguese.</p>
          </div>
          <nav className="flex flex-col gap-1" aria-label="Sections">
            {NAV_TABS.map((item) => (
              <a
                key={item.id}
                href={TAB_PATH[item.id]}
                onClick={(event) => navigate(event, item.id)}
                aria-current={tab === item.id ? 'page' : undefined}
                className={`flex cursor-pointer items-center gap-3 rounded-full px-4 py-2.5 text-left text-sm font-semibold transition-colors ${tab === item.id ? 'bg-slate-950 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'}`}
              >
                <NavGlyph id={item.id} className="size-5 shrink-0" />
                {item.label}
                {item.badge && (
                  <span className="ml-auto rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-950">
                    {item.badge}
                  </span>
                )}
              </a>
            ))}
          </nav>
        </div>
        {/* pl-12 lines the marker up with the nav labels above (button's px-4 +
            icon's size-5 + the gap-3 between icon and label = 48px), not just
            the button's own edge. The Instagram link is right-aligned and laid
            out independently of the marker, not sharing its flex row. */}
        <div className="flex items-center justify-between gap-2 pl-12">
          <p className="identity-marker">
            by<span className="maker">jeyfala</span>
          </p>
          <a
            href="https://www.instagram.com/jeyfala_/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="jeyfala on Instagram"
            className="shrink-0 text-slate-400 transition-colors hover:text-slate-600"
          >
            <InstagramIcon className="size-5" />
          </a>
        </div>
      </aside>

      {/* Compact top bar on mobile — the sidebar's nav moves to the bottom instead. */}
      <header className="border-b border-slate-200 bg-white px-4 py-4 lg:hidden">
        <p className="flex items-center gap-2 text-base font-bold tracking-tight text-slate-950">
          <span aria-hidden="true" className="text-xl leading-none">🇧🇷</span> Caderno Brasil
        </p>
        <p className="mt-0.5 text-xs text-slate-500">Notes on learning Brazilian Portuguese.</p>
      </header>

      <div className="min-w-0 flex-1 pb-24 lg:pb-0">
        {tab === 'drills' ? (
          <TabShell kicker="Practice" heading="Daily Drills" subtitle="Short exercises to practice often." topic={topic} onTopic={selectTopic}>
          {topic === 'conjugation' ? (
            <Setup settings={settings} setSettings={setSettings} onStart={start} complete={complete} score={score} />
          ) : topic === 'contraction' ? (
            <ContractionSetup ids={contractionIds} setIds={setContractionIds} hint={contractionHint} setHint={setContractionHint} onStart={start} complete={complete} score={score} />
          ) : topic === 'objectPronoun' ? (
            <ObjectPronounSetup
              register={opRegister}
              setRegister={setOpRegister}
              categories={opCategories}
              setCategories={setOpCategories}
              placements={opPlacements}
              setPlacements={setOpPlacements}
              onStart={start}
              complete={complete}
              score={score}
            />
          ) : topic === 'plurals' ? (
            <PluralsSetup ids={pluralCards} setIds={setPluralCards} onStart={start} complete={complete} score={score} />
          ) : (
            <ComparativeSetup
              categories={comparativeCategories}
              setCategories={setComparativeCategories}
              forms={comparativeForms}
              setForms={setComparativeForms}
              onStart={start}
              complete={complete}
              score={score}
            />
          )}
          </TabShell>
        ) : tab === 'reference' ? (
        <TabShell
          wide
          kicker="Grammar"
          heading="Cheat Sheet"
          subtitle={
            topic === 'conjugation'
              ? 'Verb endings and patterns.'
              : topic === 'contraction'
                ? 'Article, pronoun, and demonstrative contractions.'
                : topic === 'objectPronoun'
                  ? 'Direct, indirect and reflexive pronouns.'
                  : topic === 'comparative'
                    ? 'Comparing things using more/less/as/most/least.'
                    : topic === 'subjunctive'
                      ? 'Subjunctive use cases.'
                      : topic === 'nuances'
                        ? 'Express with precision.'
                        : topic === 'verbChoice'
                          ? 'Picking the right verb for the meaning.'
                          : 'Making nouns and adjectives plural.'
          }
          aside={topic === 'contraction' || topic === 'objectPronoun' ? <TableLegend /> : undefined}
          topic={topic}
          topics={GRAMMAR_TOPICS}
          onTopic={selectTopic}
        >
          {topic === 'conjugation' ? (
            <Tables
              section={conjSection}
              onSectionChange={setConjSection}
              showTu={showTu}
              onToggleTu={() => setShowTu((value) => !value)}
            />
          ) : topic === 'contraction' ? (
            <ContractionReference />
          ) : topic === 'objectPronoun' ? (
            <ObjectPronounReference />
          ) : topic === 'comparative' ? (
            <ComparativeReference />
          ) : topic === 'subjunctive' ? (
            <SubjunctiveReference />
          ) : topic === 'nuances' ? (
            <NuancesReference />
          ) : topic === 'verbChoice' ? (
            <VerbChoiceReference />
          ) : (
            <PluralsReference />
          )}
          </TabShell>
        ) : tab === 'vocabulary' ? (
          <VocabularyView />
        ) : tab === 'translate' ? (
          <TranslateView />
        ) : tab === 'songs' ? (
          <SongsView />
        ) : (
          <AboutView onNavigate={navigate} />
        )}

        <footer className="relative border-t border-slate-200 px-4 py-6 text-center">
          <p className="identity-marker">
            by<span className="maker">jeyfala</span>
          </p>
          {/* Positioned independently of the (centered) identity marker, not
              sharing its flex row, so it stays pinned to the right regardless
              of how wide the marker is. */}
          <a
            href="https://www.instagram.com/jeyfala_/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="jeyfala on Instagram"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600 sm:right-6"
          >
            <InstagramIcon className="size-5" />
          </a>
        </footer>
      </div>

      {/* Floating rounded bottom nav on mobile, icon above label. */}
      <nav className="fixed inset-x-4 bottom-4 z-20 flex justify-around rounded-full bg-slate-950 px-2 py-2 shadow-lg lg:hidden" aria-label="Sections">
        {NAV_TABS.map((item) => (
          <a
            key={item.id}
            href={TAB_PATH[item.id]}
            onClick={(event) => navigate(event, item.id)}
            aria-current={tab === item.id ? 'page' : undefined}
            className={`flex flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-full px-2 py-1.5 text-[11px] font-semibold transition-colors ${tab === item.id ? 'bg-white/15 text-white' : 'text-white/60'}`}
          >
            <span className="relative">
              <NavGlyph id={item.id} className="size-5" />
              {/* Mobile has no room for the full "New" pill — a small dot
                  on the icon carries the same signal more compactly. */}
              {item.badge && <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-amber-400" />}
            </span>
            {item.label}
          </a>
        ))}
      </nav>
    </div>
  )
}
